import { useRef, useState, useEffect, useContext, useLayoutEffect, useMemo } from 'react'
import { CommandBarButton, IconButton, Dialog, DialogType, DialogFooter, Stack, MessageBar, MessageBarType, PrimaryButton, Text } from '@fluentui/react'
import { SquareRegular, ShieldLockRegular, ErrorCircleRegular, CloudAddRegular, EditRegular, AttachRegular, GlobeRegular, DatabaseRegular, ChevronDownRegular, ChevronUpRegular } from '@fluentui/react-icons'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import uuid from 'react-uuid'
import { isEmpty } from 'lodash'
import DOMPurify from 'dompurify'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { nord } from 'react-syntax-highlighter/dist/esm/styles/prism'

import styles from './Chat.module.css'
import { XSSAllowTags } from '../../constants/sanatizeAllowables'
import { conversationManager } from '../../utils/conversationManager'
import { useFeatureAccess } from '../../context/FeatureAccessContext'

import {
  ChatMessage,
  ConversationRequest,
  Citation,
  ToolMessageContent,
  AzureSqlServerExecResults,
  ChatResponse,
  getUserInfo,
  Conversation,
  historyGenerate,
  historyClear,
  historyGetDocuments,
  historyRead,
  historyRemoveDocument,
  ChatHistoryLoadingState,
  CosmosDBStatus,
  ErrorMessage,
  ExecResults,
  DocumentReference,
  updatePinnedWorkspace,
} from "../../api";
import { authenticatedFetch } from "../../utils/authApi";
import { apiClient } from "../../services/apiClient";
import { buildMediaUrl } from "../../config/apiConfig";
import { Answer } from "../../components/Answer";
import { QuestionInput, QuestionInputRef } from "../../components/QuestionInput";
import { DocumentManager } from "../../components/DocumentManager";
import { ChatHistoryPanel } from "../../components/ChatHistory/ChatHistoryPanel";
import { ThinkingBubble } from "../../components/ThinkingBubble";
import { MessageActions } from "../../components/MessageActions/MessageActions";
import { ConversationActions } from "../../components/ConversationActions/ConversationActions";
import { ChatControls } from "../../components/ChatControls";
import { UserPreferencesControls } from "../../components/UserPreferences";
import { AppStateContext } from "../../state/AppProvider";
import { useAuth } from "../../components/Auth/AuthProvider";
import { useBoolean } from "@fluentui/react-hooks";
import { useAsyncUpload } from "../../hooks/useAsyncUpload";
import { DuplicateFileModal, DuplicateCheckResult } from "../../components/DuplicateFileModal";
import { ExcelSheetSelector } from "../../components/Excel";
import { UserPreferences, DataSourceRequest } from "../../api/dataSourceApi";
import { DocumentTranslationPanel, PdfConversionPanel, detectPdfToWordIntent } from "../../components/DocumentTranslation";
import { DocumentGenerationPanel } from "../../components/DocumentGeneration";
import { DocumentDraftPanel, DocumentDraft } from "../../components/DocumentDraft";
import { TenderBuilder } from "../../components/DocumentWorkspace/TenderBuilder";
import { TenderWorkspace, getWorkspace } from "../../api/documentWorkspaceApi";
import { ClarificationCards, ClarificationRequest, ClarificationOption } from "../../components/Answer/ClarificationCards";
import { debugLog } from "../../utils/debugLogger";

const enum messageStatus {
  NotRunning = 'Not Running',
  Processing = 'Processing',
  Done = 'Done'
}

/**
 * Check if a URL is clickable/valid for user access.
 * Allow all valid http/https URLs to be clickable.
 */
const isClickableUrl = (url: string): boolean => {
  if (!url || url === '') return false;
  
  // Allow any URL that starts with http:// or https://
  return url.startsWith('http://') || url.startsWith('https://');
};

const Chat = () => {
  const appStateContext = useContext(AppStateContext)
  const { authConfig, user } = useAuth()
  const { checkModelAccess, isFeatureLoaded } = useFeatureAccess()
  const models = appStateContext?.state.models || [];
  const allModelOptions = appStateContext?.state.modelOptions || [];
  const selectedModel = appStateContext?.state.selectedModel || '';

  // Filter model options based on user's feature permissions
  const modelOptions = useMemo(() => {
    if (!isFeatureLoaded) {
      // While loading, show all models (fail open for UX)
      return allModelOptions;
    }
    return allModelOptions.filter(model => checkModelAccess(model.id));
  }, [allModelOptions, checkModelAccess, isFeatureLoaded]);

  // Personalized greeting based on time of day and user name
  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    let timeGreeting: string;
    
    if (hour < 12) {
      timeGreeting = 'Good morning';
    } else if (hour < 17) {
      timeGreeting = 'Good afternoon';
    } else {
      timeGreeting = 'Good evening';
    }
    
    // Get first name from displayName (e.g., "John Doe" -> "John")
    const firstName = user?.displayName?.split(' ')[0] || '';
    
    return firstName ? `${timeGreeting}, ${firstName}` : timeGreeting;
  }, [user?.displayName]);

  const ui = appStateContext?.state.frontendSettings?.ui
  const AUTH_ENABLED = appStateContext?.state.frontendSettings?.auth_enabled
  const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null)
  const questionInputEmptyRef = useRef<QuestionInputRef>(null)
  const questionInputChatRef = useRef<QuestionInputRef>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)  // Add ref for hidden file input
  const translateFileInputRef = useRef<HTMLInputElement>(null)  // Ref for direct translation file input
  
  // Initialize async upload hook
  const { startUpload, startUploadWithSheets, isExcelFile, getAllUploads, getUploadProgress, removeUpload, cancelUpload, cleanup, checkDuplicates, reuseDocument, deleteExistingUpload } = useAsyncUpload();
  
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [showLoadingMessage, setShowLoadingMessage] = useState<boolean>(false)
  const [activeCitation, setActiveCitation] = useState<Citation>()
  const [isCitationPanelOpen, setIsCitationPanelOpen] = useState<boolean>(false)
  const [isIntentsPanelOpen, setIsIntentsPanelOpen] = useState<boolean>(false)
  const abortFuncs = useRef([] as AbortController[])
  const [showAuthMessage, setShowAuthMessage] = useState<boolean | undefined>()
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [execResults, setExecResults] = useState<ExecResults[]>([])
  const [processMessages, setProcessMessages] = useState<messageStatus>(messageStatus.NotRunning)
  const [clearingChat, setClearingChat] = useState<boolean>(false)
  const [clearQuestionInputFiles, setClearQuestionInputFiles] = useState<boolean>(false)
  const [sharedUploadedFiles, setSharedUploadedFiles] = useState<any[]>([])
  const [hideErrorDialog, { toggle: toggleErrorDialog }] = useBoolean(true)
  const [errorMsg, setErrorMsg] = useState<ErrorMessage | null>()
  const [logo, setLogo] = useState('')
  const [answerId, setAnswerId] = useState<string>('')
  const [pdfUploaded, setPdfUploaded] = useState(false);

  // Add state for new upload flow
  const [uploadedFileId, setUploadedFileId] = useState<string | null>(null);
  const [filePreview, setFilePreview] = useState<string>('');
  const [question, setQuestion] = useState<string>('');
  const [answer, setAnswer] = useState<string>('');
  // Imagen controls
  const [imagenAspectRatio, setImagenAspectRatio] = useState<string>('1:1');
  const [imagenCount, setImagenCount] = useState<number>(1);
  // GPT Image 1.5 controls - empty means use admin defaults
  const [dalleSize, setDalleSize] = useState<string>(''); // Empty = admin default
  const [dalleStyle, setDalleStyle] = useState<string>(''); // Only used for legacy DALL-E
  const [dalleQuality, setDalleQuality] = useState<string>(''); // Empty = admin default
  const [previewImage, setPreviewImage] = useState<{ src: string; alt?: string } | null>(null);
  const [webSearchMode, setWebSearchMode] = useState<boolean>(false);
  const [modelDropdownOpen, setModelDropdownOpen] = useState<boolean>(false);
  const [selectedDataSource, setSelectedDataSource] = useState<string | null>(null);
  const [userPreferences, setUserPreferences] = useState<UserPreferences>({
    use_dynamic_system_message: false
  });
  const modelDropdownRef = useRef<HTMLDivElement>(null);
  const [intentReasoning, setIntentReasoning] = useState<{data_source: string, reason: string, confidence: number, thinking: string} | null>(null);
  const [isDragOverChat, setIsDragOverChat] = useState<boolean>(false);
  const [isUploadingFiles, setIsUploadingFiles] = useState<boolean>(false);
  const [uploadingFilesCount, setUploadingFilesCount] = useState<number>(0);
  const [documentRemovalNotification, setDocumentRemovalNotification] = useState<{type: string, message: string, reason: string, removed_count: number} | null>(null);
  // Duplicate detection state
  const [showDuplicateModal, setShowDuplicateModal] = useState<boolean>(false);
  const [duplicateCheckResults, setDuplicateCheckResults] = useState<DuplicateCheckResult[]>([]);
  const [pendingUploadFiles, setPendingUploadFiles] = useState<File[]>([]);
  const [isCheckingDuplicates, setIsCheckingDuplicates] = useState<boolean>(false);
  // Excel sheet selector state
  const [showExcelSheetSelector, setShowExcelSheetSelector] = useState<boolean>(false);
  const [pendingExcelFile, setPendingExcelFile] = useState<File | null>(null);
  const [pendingNonExcelFiles, setPendingNonExcelFiles] = useState<File[]>([]);
  const [showThinkingProcess, setShowThinkingProcess] = useState<boolean>(false);
  const [showCacheBadge, setShowCacheBadge] = useState<boolean>(true);  // Admin toggle for cache badge visibility
  // Document Translation state
  const [showTranslationPanel, setShowTranslationPanel] = useState<boolean>(false);
  const [translationPanelMessage, setTranslationPanelMessage] = useState<string>('');
  const [detectedTargetLanguage, setDetectedTargetLanguage] = useState<string | null>(null);
  // PDF to Word conversion state
  const [showPdfConversionPanel, setShowPdfConversionPanel] = useState<boolean>(false);
  const [pdfConversionNotification, setPdfConversionNotification] = useState<{
    status: 'converting' | 'success' | 'error';
    filename?: string;
    downloadUrl?: string;
    error?: string;
  } | null>(null);
  const [streamingContent, setStreamingContent] = useState<string>(''); // For streaming token display
  const [isStreaming, setIsStreaming] = useState<boolean>(false); // Whether we're actively streaming
  // Clarification UI state - when system needs user to select source/intent
  const [pendingClarification, setPendingClarification] = useState<ClarificationRequest | null>(null);
  // Token limit exceeded dialog state
  const [showTokenLimitDialog, setShowTokenLimitDialog] = useState<boolean>(false);
  const [tokenLimitError, setTokenLimitError] = useState<{blockedBy: string; reason: string} | null>(null);
  // Document Generation panel state
  const [showDocumentGenerationPanel, setShowDocumentGenerationPanel] = useState<boolean>(false);
  const [documentGenerationResult, setDocumentGenerationResult] = useState<{document_url: string; document_id: string; filename: string; format: string} | null>(null);
  // Document Draft co-authoring panel state
  const [showDocumentDraftPanel, setShowDocumentDraftPanel] = useState<boolean>(false);
  const [currentDraftId, setCurrentDraftId] = useState<string | null>(null);
  const [currentWorkspace, setCurrentWorkspace] = useState<TenderWorkspace | null>(null);  // Track workspace for tender mode detection
  const [draftRefreshKey, setDraftRefreshKey] = useState<number>(0);  // Increment to trigger panel refresh
  const [isDocumentPanelCollapsed, setIsDocumentPanelCollapsed] = useState<boolean>(false);  // Collapsed state
  const [pinnedWorkspaceId, setPinnedWorkspaceId] = useState<string | null>(null);  // Pinned workspace persists across chat
  const [hasDocumentUpdate, setHasDocumentUpdate] = useState<boolean>(false);  // Show update indicator on collapsed panel

  // Helper function to safely show intent reasoning during loading phase
  const showIntentReasoningDuringLoading = (reasoning: any) => {
    console.log('[Chat] Intent reasoning displayed during loading:', reasoning);
    setIntentReasoning(reasoning);
    // Don't auto-hide during loading - it will be hidden when response arrives
  };

  // Helper function to hide loading and clear intent reasoning
  const hideLoadingMessage = () => {
    setShowLoadingMessage(false);
    setIntentReasoning(null);
  };

  // Helper: download a data URL as a file
  const downloadDataUrl = (dataUrl: string, filename: string) => {
    try {
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      console.error('Failed to download image', e);
    }
  };

  const errorDialogContentProps = {
    type: DialogType.close,
    title: errorMsg?.title,
    closeButtonAriaLabel: 'Close',
    subText: errorMsg?.subtitle
  }

  // Helper function to transform backend document metadata into QuestionInput format
  const transformBackendDocuments = (backendDocs: any[]) => {
    return backendDocs.map(doc => {
      // Check processing status from backend
      const processingStatus = doc.processing_status || doc.processingStatus;
      const isCompleted = processingStatus === 'completed' || !processingStatus;
      const isProcessing = processingStatus === 'processing' || processingStatus === 'queued' || processingStatus === 'uploading';
      const isFailed = processingStatus === 'failed';
      
      return {
        id: doc.id,
        fileId: doc.id, // For tracking
        filename: doc.filename,
        type: doc.filetype?.toLowerCase().includes('image') ? 'image' : 'document',
        uploaded: isCompleted && !isFailed,
        uploading: isProcessing,
        processing: isProcessing,
        processingStatus: processingStatus,
        processingProgress: doc.processing_progress || doc.processingProgress,
        error: isFailed ? (doc.processing_message || 'Processing failed') : undefined,
        preview: doc.preview,
        // Note: no 'file' property since these come from backend
      };
    });
  };
  
  // Helper function to clear document-related state
  const clearDocumentContext = () => {
    debugLog("[Chat] Clearing document context");
    setSharedUploadedFiles([]);
    setPdfUploaded(false);
    // Force QuestionInput to clear files by setting flag
    setClearQuestionInputFiles(true);
    // Reset the flag after a delay
    setTimeout(() => setClearQuestionInputFiles(false), 200);
  };

  // Helper function to upload files and update document state
  const uploadFiles = async (files: File[]) => {
    // Validate large .xls files - they cannot be processed safely
    const XLS_SIZE_LIMIT = 5 * 1024 * 1024; // 5MB threshold for .xls files
    const largeXlsFiles = files.filter(f => {
      const name = f.name.toLowerCase();
      // Check for .xls but NOT .xlsx or .xlsm (which are OK)
      return name.endsWith('.xls') && !name.endsWith('.xlsx') && !name.endsWith('.xlsm') && f.size > XLS_SIZE_LIMIT;
    });
    
    if (largeXlsFiles.length > 0) {
      const fileNames = largeXlsFiles.map(f => f.name).join(', ');
      alert(`Large .xls files are not supported: ${fileNames}\n\nPlease open the file in Excel and save as .xlsx or CSV, then upload again.\n\n(Files under 5MB in .xls format are supported)`);
      // Filter out the large .xls files and continue with the rest
      files = files.filter(f => !largeXlsFiles.includes(f));
      if (files.length === 0) return;
    }
    
    // Check for Excel files - show sheet selector for the first Excel file found
    const excelFile = files.find(f => isExcelFile(f.name));
    const nonExcelFiles = files.filter(f => !isExcelFile(f.name));
    
    if (excelFile) {
      debugLog(`[Chat] Excel file detected: ${excelFile.name}, showing sheet selector`);
      setPendingExcelFile(excelFile);
      setPendingNonExcelFiles(nonExcelFiles);
      setShowExcelSheetSelector(true);
      return; // Wait for user to select sheets
    }
    
    // No Excel files - proceed with normal upload
    await uploadFilesInternal(files);
  };

  // Internal upload function (after sheet selection for Excel)
  const uploadFilesInternal = async (files: File[], excelFileWithSheets?: { file: File; selectedSheets: string[] }) => {
    const allFiles = excelFileWithSheets ? [...files, excelFileWithSheets.file] : files;
    setIsUploadingFiles(true);
    setUploadingFilesCount(allFiles.length);
    
    debugLog(`[Chat] Starting upload of ${allFiles.length} file(s)`);
    
    try {
      // Upload non-Excel files normally
      const nonExcelUploadPromises = files.map(async (file) => {
        debugLog(`[Chat] Uploading: ${file.name}`);
        
        const fileId = await startUpload(file);
        
        if (!fileId) {
          console.error('Failed to start async upload for:', file.name);
          return null;
        }

        debugLog(`[Chat] Upload started: ${file.name}`);
        
        // Convert to the format expected by sharedUploadedFiles
        const extension = file.name.toLowerCase().split('.').pop() || '';
        let type = 'document';
        
        if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension)) {
          type = 'image';
        } else if (['txt', 'md', 'rtf'].includes(extension)) {
          type = 'text';
        } else if (['xls', 'xlsx', 'csv'].includes(extension)) {
          type = 'spreadsheet';
        }

        return {
          id: fileId,
          fileId: fileId, // For async tracking
          filename: file.name,
          type: type,
          size: file.size,
          uploaded: false, // Will be updated by progress tracking
          uploading: true,
          processing: true, // File is being processed
          processingStatus: 'uploading',
          processingProgress: 0,
          preview: '',
          file: file
        };
      });
      
      // Upload Excel file with selected sheets if provided
      const excelUploadPromise = excelFileWithSheets ? (async () => {
        debugLog(`[Chat] Uploading Excel with selected sheets: ${excelFileWithSheets.file.name}, sheets: ${excelFileWithSheets.selectedSheets.join(', ')}`);
        
        const fileId = await startUploadWithSheets(excelFileWithSheets.file, excelFileWithSheets.selectedSheets);
        
        if (!fileId) {
          console.error('Failed to start async upload for Excel:', excelFileWithSheets.file.name);
          return null;
        }

        debugLog(`[Chat] Excel upload started: ${excelFileWithSheets.file.name}`);
        
        return {
          id: fileId,
          fileId: fileId,
          filename: excelFileWithSheets.file.name,
          type: 'spreadsheet',
          size: excelFileWithSheets.file.size,
          uploaded: false,
          uploading: true,
          processing: true,
          processingStatus: 'uploading',
          processingProgress: 0,
          preview: '',
          file: excelFileWithSheets.file,
          selectedSheets: excelFileWithSheets.selectedSheets
        };
      })() : Promise.resolve(null);
      
      const uploadResults = await Promise.all([...nonExcelUploadPromises, excelUploadPromise]);
      const successfulUploads = uploadResults.filter(result => result !== null);
      
      if (successfulUploads.length > 0) {
        setSharedUploadedFiles(prev => [...prev, ...successfulUploads]);
        setPdfUploaded(true);
        debugLog(`[Chat] Successfully started upload for ${successfulUploads.length} files`);
        
        // Save document references to conversation if we have one
        const currentConversation = appStateContext?.state.currentChat;
        if (currentConversation?.id) {
          try {
            const { historyAddDocument } = await import('../../api');
            for (const fileMeta of successfulUploads) {
              if (!fileMeta) continue;
              await historyAddDocument(currentConversation.id, {
                id: fileMeta.id || fileMeta.fileId,
                fileId: fileMeta.fileId || fileMeta.id,
                filename: fileMeta.filename,
                type: fileMeta.type || 'document',
                size: fileMeta.size || 0,
                uploadDate: new Date().toISOString()
              });
              debugLog(`[Chat] Saved document ${fileMeta.filename} to conversation ${currentConversation.id}`);
            }
          } catch (error) {
            console.error('[Chat] Failed to save document references:', error);
          }
        }
      }
      
      if (successfulUploads.length < files.length) {
        console.warn(`[Chat] ${files.length - successfulUploads.length} file(s) failed to start upload`);
      }
      
    } catch (error) {
      console.error('[Chat] Error starting uploads:', error);
    } finally {
      setIsUploadingFiles(false);
      setUploadingFilesCount(0);
      debugLog('[Chat] Upload initiation completed');
    }
  };

  // Handle Excel sheet selection confirmation
  const handleExcelSheetConfirm = async (selectedSheets: string[], processAll: boolean) => {
    debugLog(`[Chat] Excel sheet selection confirmed: processAll=${processAll}, sheets=${selectedSheets.join(', ')}`);
    setShowExcelSheetSelector(false);
    
    if (pendingExcelFile) {
      // Upload with selected sheets (or all sheets if processAll)
      const sheetsToProcess = processAll ? [] : selectedSheets; // Empty array = all sheets on backend
      await uploadFilesInternal(pendingNonExcelFiles, { 
        file: pendingExcelFile, 
        selectedSheets: sheetsToProcess 
      });
    }
    
    // Clear pending state
    setPendingExcelFile(null);
    setPendingNonExcelFiles([]);
  };

  // Handle Excel sheet selection cancellation
  const handleExcelSheetCancel = () => {
    debugLog('[Chat] Excel sheet selection cancelled');
    setShowExcelSheetSelector(false);
    setPendingExcelFile(null);
    setPendingNonExcelFiles([]);
  };

  // Cleanup async uploads on unmount AND on page unload/refresh
  // This prevents memory issues from processing files when user navigates away
  // NOTE: We use useRef for getAllUploads to avoid re-running effect on every upload state change
  const getAllUploadsRef = useRef(getAllUploads);
  getAllUploadsRef.current = getAllUploads;  // Always keep ref up to date
  
  useEffect(() => {
    // Function to cancel all in-progress uploads
    const cancelInProgressUploads = () => {
      const currentUploads = getAllUploadsRef.current();  // Use ref instead of closure
      const inProgressUploads = currentUploads.filter(
        u => u.status === 'uploading' || u.status === 'queued' || u.status === 'processing'
      );
      
      if (inProgressUploads.length > 0) {
        console.log(`[Chat] Cancelling ${inProgressUploads.length} in-progress uploads on page unload`);
        
        // Fire and forget - don't await, page is unloading
        for (const upload of inProgressUploads) {
          try {
            // Use sendBeacon for reliable delivery during page unload
            // sendBeacon only supports POST, so use the cancel-upload endpoint
            if (navigator.sendBeacon) {
              navigator.sendBeacon(`/cancel-upload/${upload.fileId}`, '');
            }
            // Also try fetch with keepalive for DELETE (may not complete but worth trying)
            fetch(`/delete-upload/${upload.fileId}`, { 
              method: 'DELETE', 
              keepalive: true,
              credentials: 'include'
            }).catch(() => {});
          } catch (e) {
            // Ignore errors during unload
          }
        }
      }
    };
    
    // Handle page unload (refresh, close, navigate away)
    const handleBeforeUnload = () => {
      cancelInProgressUploads();
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      cleanup();
    };
  }, [cleanup]);  // Only cleanup as dependency - getAllUploads accessed via ref

  // Sync async upload progress with sharedUploadedFiles
  // This effect polls getAllUploads and updates sharedUploadedFiles when status changes
  useEffect(() => {
    const syncInterval = setInterval(() => {
      const currentUploads = getAllUploads();
      
      if (currentUploads.length === 0) return;
      
      setSharedUploadedFiles(prev => {
        let hasChanges = false;
        const updated = prev.map(file => {
          const uploadProgress = currentUploads.find(u => u.fileId === file.id);
          
          if (uploadProgress) {
            // Check if file processing is complete
            const isCompleted = uploadProgress.status === 'completed';
            const isFailed = uploadProgress.status === 'failed';
            const isProcessing = uploadProgress.status === 'processing' || uploadProgress.status === 'queued';
            
            // Check if status changed OR if we got new sheet info
            const hasSheetUpdate = uploadProgress.available_sheets && !file.available_sheets;
            
            if (file.uploading !== !isCompleted || file.uploaded !== isCompleted || hasSheetUpdate) {
              hasChanges = true;
              
              return {
                ...file,
                uploaded: isCompleted,
                uploading: !isCompleted && !isFailed,
                processing: isProcessing,
                processingProgress: uploadProgress.progress,
                processingStatus: uploadProgress.status,
                error: uploadProgress.error,
                // Excel multi-sheet support - sync from upload status
                available_sheets: uploadProgress.available_sheets || file.available_sheets,
                sheet_name: file.sheet_name || uploadProgress.sheet_name // Keep user selection if exists
              };
            }
          }
          return file;
        });
        
        return hasChanges ? updated : prev;
      });
    }, 1000); // Check every second
    
    return () => clearInterval(syncInterval);
  }, [getAllUploads]);

  // Check if any files are still processing (for UI feedback)
  const hasFilesProcessing = sharedUploadedFiles.some(f => f.uploading || f.processing);

  // Global drag and drop handlers for the chat interface
  const handleChatDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Don't show drag feedback if upload is disabled
    if (!isLoading && !isUploadingFiles && ui?.show_upload_button === true) {
      setIsDragOverChat(true);
    }
  };

  const handleChatDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only clear drag state if we're leaving the chat container entirely
    if (e.currentTarget.contains(e.relatedTarget as Node) === false) {
      setIsDragOverChat(false);
    }
  };

  const handleChatDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOverChat(false);
    
    if (isLoading || isUploadingFiles) return;
    
    // Check if file upload is disabled
    if (ui?.show_upload_button === false) {
      console.log('[Chat] File upload is disabled via UI settings');
      return;
    }

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    console.log(`[Chat] Files dropped: ${files.map(f => f.name).join(', ')}`);
    
    // Validate file types - same as QuestionInput accept attribute
    const SUPPORTED_EXTENSIONS = [
      '.pdf', '.docx', '.doc', '.xlsx', '.xls', '.xlsm', '.xlsb',
      '.pptx', '.ppt', '.txt', '.jpg', '.jpeg', '.png', '.gif', '.webp'
    ];
    
    const validFiles: File[] = [];
    const invalidFiles: string[] = [];
    
    for (const file of files) {
      const ext = '.' + file.name.split('.').pop()?.toLowerCase();
      if (SUPPORTED_EXTENSIONS.includes(ext)) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file.name);
      }
    }
    
    if (invalidFiles.length > 0) {
      console.warn(`[Chat] Unsupported file types: ${invalidFiles.join(', ')}`);
      // Show user-friendly error message
      const supportedList = 'PDF, Word, Excel, PowerPoint, images, and text files';
      alert(`Cannot upload: ${invalidFiles.join(', ')}\n\nSupported formats: ${supportedList}`);
    }
    
    if (validFiles.length > 0) {
      await uploadFiles(validFiles);
    }
  };

  // Handle model selection
  const handleModelSelection = async (modelId: string) => {
    console.log('Model selection:', modelId, 'Current selected:', selectedModel);
    
    // Update AppState immediately for instant UI feedback
    appStateContext?.dispatch({ type: 'SET_SELECTED_MODEL', payload: modelId });
    setModelDropdownOpen(false);
    
    // Save model preference to backend
    try {
      console.log('[Chat] Saving model preference:', modelId);
      const { updateUserPreferences } = await import('../../api/dataSourceApi');
      await updateUserPreferences({ selected_model: modelId });
      console.log('[Chat] Successfully saved model preference');
      
      // Ensure AppState stays synced (redundant but safe)
      appStateContext?.dispatch({ type: 'SET_SELECTED_MODEL', payload: modelId });
    } catch (error) {
      console.error('[Chat] Failed to save model preference:', error);
      // Revert UI state on save failure
      appStateContext?.dispatch({ type: 'SET_SELECTED_MODEL', payload: selectedModel });
    }
  };
  
  // Handle data source selection
  const handleDataSourceSelection = (dataSourceId: string | null) => {
    setSelectedDataSource(dataSourceId);
  };
  
  // Initialize selected data source from user preferences
  useEffect(() => {
    const loadUserSelectedDataSource = async () => {
      try {
        const { getUserPreferences } = await import('../../api/dataSourceApi');
        const preferences = await getUserPreferences();
        
        if (preferences.selected_data_source !== undefined) {
          setSelectedDataSource(preferences.selected_data_source);
        }
      } catch (error) {
        console.error('[Chat] Failed to load selected data source from preferences:', error);
      }
    };
    
    loadUserSelectedDataSource();
  }, []);
  
  // Handle user preferences change
  const handleUserPreferencesChange = (preferences: UserPreferences) => {
    // Note: This fires frequently from ChatControls sync, avoid logging
    setUserPreferences(preferences);
  };

  // Close model dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modelDropdownOpen && modelDropdownRef.current && !modelDropdownRef.current.contains(event.target as Node)) {
        setModelDropdownOpen(false);
      }
    };

    if (modelDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [modelDropdownOpen]);
  
  const modalProps = {
    titleAriaId: 'labelId',
    subtitleAriaId: 'subTextId',
    isBlocking: true,
    styles: { main: { maxWidth: 450 } }
  }

  const [ASSISTANT, TOOL, ERROR] = ['assistant', 'tool', 'error']
  const NO_CONTENT_ERROR = 'No content in messages object.'

  useEffect(() => {
    // Disabled: Hide CosmosDB error dialog for now
    // if (
    //   appStateContext?.state.isCosmosDBAvailable?.status !== CosmosDBStatus.Working &&
    //   appStateContext?.state.isCosmosDBAvailable?.status !== CosmosDBStatus.NotConfigured &&
    //   appStateContext?.state.chatHistoryLoadingState === ChatHistoryLoadingState.Fail &&
    //   hideErrorDialog
    // ) {
    //   let subtitle = `${appStateContext.state.isCosmosDBAvailable.status}. Please contact the site administrator.`
    //   setErrorMsg({
    //     title: 'Chat history is not enabled',
    //     subtitle: subtitle
    //   })
    //   toggleErrorDialog()
    // }
  }, [appStateContext?.state.isCosmosDBAvailable])

  const handleErrorDialogClose = () => {
    toggleErrorDialog()
    setTimeout(() => {
      setErrorMsg(null)
    }, 500)
  }

  useEffect(() => {
    // Update chat logo whenever frontend settings change
    const fsui = appStateContext?.state.frontendSettings?.ui
    if (fsui) {
      setLogo((fsui as any).chat_logo || (fsui as any).logo || '')
    } else if (!appStateContext?.state.isLoading) {
      setLogo(ui?.chat_logo || ui?.logo || '')
    }
  }, [appStateContext?.state.frontendSettings, appStateContext?.state.isLoading])
  
  // Listen for document loaded events from history selection
  useEffect(() => {
    const handleDocumentsLoaded = (event: CustomEvent<{ documents: any[] }>) => {
      if (event.detail.documents && event.detail.documents.length > 0) {
        // Check if documents are already in the correct format or need transformation
        const documents = event.detail.documents;
        const needsTransformation = documents.some(doc => !doc.file && doc.filename);
        
        setSharedUploadedFiles(needsTransformation ? transformBackendDocuments(documents) : documents);
        setPdfUploaded(true);
      }
    };
    
    // Handle clearing documents when switching to a conversation without documents
    const handleClearDocuments = () => {
      clearDocumentContext();
    };
    
    window.addEventListener('documentsLoaded', handleDocumentsLoaded as EventListener);
    window.addEventListener('clearDocuments', handleClearDocuments as EventListener);
    
    return () => {
      window.removeEventListener('documentsLoaded', handleDocumentsLoaded as EventListener);
      window.removeEventListener('clearDocuments', handleClearDocuments as EventListener);
    };
  }, []) // clearDocumentContext is defined in component scope, no need to add it to dependencies

  useEffect(() => {
    setIsLoading(appStateContext?.state.chatHistoryLoadingState === ChatHistoryLoadingState.Loading)
  }, [appStateContext?.state.chatHistoryLoadingState])
  
  // Monitor the current chat for changes and update document context
  useEffect(() => {
    const currentChat = appStateContext?.state.currentChat;
    
    debugLog('[Chat] useEffect triggered - currentChat changed:', {
      'currentChat.id': currentChat?.id,
      'has messages': currentChat?.messages?.length || 0,
      'current sharedUploadedFiles': sharedUploadedFiles?.length || 0
    });
    
    const checkForDocuments = async () => {
      if (currentChat && currentChat.id) {
        // If we have a current chat with a valid ID, check if we need to update the document context
        try {
          debugLog(`[Chat] Checking for documents in conversation: ${currentChat.id}`);
          // Try to get documents for this conversation
          const documents = await historyGetDocuments(currentChat.id);
          
          debugLog(`[Chat] historyGetDocuments returned:`, documents);
          
          if (documents && documents.length > 0) {
            // This conversation has documents, update the context
            debugLog(`[Chat] Found ${documents.length} documents, updating context`);
            setSharedUploadedFiles(transformBackendDocuments(documents));
            setPdfUploaded(true);
          } else {
            // This conversation has no documents, clear the context ONLY if we're switching to a different conversation
            // Don't clear if we're just in the same conversation without loaded documents yet
            const currentConversationId = appStateContext?.state.currentChat?.id;
            const hasCurrentDocuments = sharedUploadedFiles && sharedUploadedFiles.length > 0;
            
            // 🎯 CRITICAL FIX: Don't clear documents if we have them and no valid conversation ID yet
            if (!hasCurrentDocuments || (!currentConversationId && currentChat.id)) {
              debugLog(`[Chat] No documents found for conversation ${currentChat.id}, clearing context`);
              clearDocumentContext();
            } else {
              debugLog(`[Chat] Preserving existing document context for conversation continuity`);
            }
          }
          
          // Check for pinned workspace and auto-open if present
          const historyResult = await historyRead(currentChat.id);
          if (historyResult?.pinnedWorkspaceId) {
            debugLog(`[Chat] Found pinned workspace: ${historyResult.pinnedWorkspaceId}`);
            
            // Only auto-expand if this is a NEW pinned workspace (conversation switch)
            // Don't re-expand if user manually collapsed an existing pinned workspace
            const isNewPinnedWorkspace = pinnedWorkspaceId !== historyResult.pinnedWorkspaceId;
            
            setPinnedWorkspaceId(historyResult.pinnedWorkspaceId);
            setCurrentDraftId(historyResult.pinnedWorkspaceId);
            setShowDocumentDraftPanel(true);
            
            if (isNewPinnedWorkspace) {
              // Only expand for newly discovered pinned workspace
              setIsDocumentPanelCollapsed(false);
              debugLog(`[Chat] New pinned workspace - auto-expanding panel`);
            } else {
              debugLog(`[Chat] Same pinned workspace - preserving collapsed state`);
            }
          } else {
            // Clear pinned workspace state when switching to conversation without one
            setPinnedWorkspaceId(null);
          }
        } catch (error) {
          console.error(`[Chat] Failed to check for documents in conversation ${currentChat.id}:`, error);
          // 🎯 CRITICAL FIX: Don't clear documents on error if we have some
          if (!sharedUploadedFiles || sharedUploadedFiles.length === 0) {
            clearDocumentContext();
          } else {
            debugLog(`[Chat] Preserving documents despite error checking conversation ${currentChat.id}`);
          }
        }
      } else if (currentChat && !currentChat.id) {
        // 🎯 CRITICAL FIX: If we have a conversation but no ID (new conversation), don't clear documents yet
        debugLog(`[Chat] Conversation exists but no ID yet, preserving document context`);
      } else {
        // If there's no current chat at all, ensure document context is cleared
        debugLog(`[Chat] No current chat, clearing document context`);
        clearDocumentContext();
        // Also clear pinned workspace state
        setPinnedWorkspaceId(null);
      }
    };
    
    // Always run this check when current chat changes, even if it becomes null
    checkForDocuments();
  }, [appStateContext?.state.currentChat])

  // Fetch admin settings for AI thinking process and cache badge visibility
  useEffect(() => {
    const fetchDisplaySettings = async () => {
      try {
        // Use public config endpoint (no admin auth required)
        const publicConfig = await apiClient.getPublicConfig();
        
        // Simple flat structure from public config
        const shouldShowThinking = publicConfig.SHOW_INTENT_REASONING === true || 
                                   publicConfig.SHOW_INTENT_REASONING === 'true' || 
                                   publicConfig.SHOW_INTENT_REASONING === 1;
        const shouldShowCacheBadge = publicConfig.SHOW_CACHE_BADGE !== false && 
                                     publicConfig.SHOW_CACHE_BADGE !== 'false' && 
                                     publicConfig.SHOW_CACHE_BADGE !== 0;
        
        setShowThinkingProcess(shouldShowThinking);
        setShowCacheBadge(shouldShowCacheBadge);
        debugLog('[Chat] 🧠 Thinking process setting:', shouldShowThinking);
        debugLog('[Chat] ⚡ Cache badge setting:', shouldShowCacheBadge);
      } catch (error) {
        // Default to showing thinking but hiding cache badge on error
        setShowThinkingProcess(false);
        setShowCacheBadge(true);
        console.warn('[Chat] Failed to fetch display settings:', error);
      }
    };
    fetchDisplaySettings();
  }, []);

  // Application startup optimization - preload critical data
  useEffect(() => {
    const initializeApp = async () => {
      try {
        debugLog('[Chat] 🚀 [OPTIMIZATION] Preloading critical endpoints...');
        await apiClient.preloadFrequentEndpoints();
        debugLog('[Chat] ✅ [OPTIMIZATION] Critical data preloaded');
      } catch (error) {
        console.warn('[Chat] ⚠️ [OPTIMIZATION] Preload failed, continuing with normal flow:', error);
      }
    };
    initializeApp();
  }, []);

  const getUserInfoList = async () => {
    if (!AUTH_ENABLED) {
      setShowAuthMessage(false)
      return
    }
    const userInfoList = await getUserInfo()
    if (userInfoList.length === 0 && window.location.hostname !== '127.0.0.1') {
      setShowAuthMessage(true)
    } else {
      setShowAuthMessage(false)
    }
  }

  let assistantMessage = {} as ChatMessage
  let toolMessage = {} as ChatMessage
  let assistantContent = ''

  useEffect(() => parseExecResults(execResults), [execResults])

  const parseExecResults = (exec_results_: any): void => {
    if (exec_results_ == undefined) return
    const exec_results = exec_results_.length === 2 ? exec_results_ : exec_results_.splice(2)
    appStateContext?.dispatch({ type: 'SET_ANSWER_EXEC_RESULT', payload: { answerId: answerId, exec_result: exec_results } })
  }

  const processResultMessage = (resultMessage: ChatMessage, userMessage: ChatMessage, conversationId?: string, localConversation?: any, sourceFromIntent?: string, confidenceFromIntent?: number, intentReasoning?: any) => {

    
    if (typeof resultMessage.content === "string" && resultMessage.content.includes('all_exec_results')) {
      const parsedExecResults = JSON.parse(resultMessage.content) as AzureSqlServerExecResults
      setExecResults(parsedExecResults.all_exec_results)
      assistantMessage.context = JSON.stringify({
        all_exec_results: parsedExecResults.all_exec_results
      })
    }

    if (resultMessage.role === ASSISTANT) {
      setAnswerId(resultMessage.id)
      assistantContent += resultMessage.content
      assistantMessage = { ...assistantMessage, ...resultMessage }
      assistantMessage.content = assistantContent
      
      // Preserve document_metadata if it exists on the incoming message
      if ((resultMessage as any).document_metadata) {
        (assistantMessage as any).document_metadata = (resultMessage as any).document_metadata;
      }
      
      // Add source information from intent reasoning if available
      if (sourceFromIntent) {
        (assistantMessage as any).source = sourceFromIntent;
        if (confidenceFromIntent !== undefined) {
          (assistantMessage as any).sourceConfidence = confidenceFromIntent;
        }
      }
      
      // Store full intent reasoning data for thinking process indicator
      if (intentReasoning) {
        (assistantMessage as any).intent_reasoning = intentReasoning;
        // Also set source from intent reasoning if not already set
        if (!sourceFromIntent && intentReasoning.data_source) {
          (assistantMessage as any).source = intentReasoning.data_source;
          if (intentReasoning.confidence !== undefined) {
            (assistantMessage as any).sourceConfidence = intentReasoning.confidence;
          }
        }
      }

      if (resultMessage.context) {
        toolMessage = {
          id: uuid(),
          role: TOOL,
          content: resultMessage.context,
          date: new Date().toISOString()
        }
      }
    }

    if (resultMessage.role === TOOL) toolMessage = resultMessage

    const updateWithDedup = (existing: ChatMessage[]): ChatMessage[] => {
      // Always start with the current complete message state to prevent message loss
      let updated = [...existing]
      
      // Ensure user message is present if not already in the array
      const hasUserMessage = updated.some(m => m.role === 'user' && m.id === userMessage.id)
      if (!hasUserMessage) {
        // Insert user message before any assistant messages
        const firstAssistantIdx = updated.findIndex(m => m.role === ASSISTANT)
        if (firstAssistantIdx >= 0) {
          updated.splice(firstAssistantIdx, 0, userMessage)
        } else {
          updated.push(userMessage)
        }
      }
      
      // Optionally add tool message once
      if (!isEmpty(toolMessage)) {
        const hasSameTool = updated.some(m => m.role === TOOL && typeof m.content === 'string' && m.content === toolMessage.content)
        if (!hasSameTool) {
          updated.push(toolMessage)
        }
      }
      // Update or append assistant message
      const lastAssistantIdx = [...updated].map(m => m.role).lastIndexOf(ASSISTANT)
      if (lastAssistantIdx >= 0 && updated[lastAssistantIdx]?.id === assistantMessage.id) {
        updated[lastAssistantIdx] = { ...assistantMessage }
      } else {
        updated.push({ ...assistantMessage })
      }
      return updated
    }

    if (!conversationId) {
      // Even without conversation ID, we should still update the current chat if it exists
      const currentConversation = appStateContext?.state.currentChat
      if (currentConversation) {
        // Always use the current complete message state as the base
        const baseMessages = messages.length > 0 ? messages : (currentConversation.messages || [])
        const updatedMessages = updateWithDedup(baseMessages)
        const updatedConversation = { ...currentConversation, messages: updatedMessages }
        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: updatedConversation })
        setMessages(updatedMessages)
      } else {
        // Ensure we preserve all existing messages
        const baseMessages = messages.length > 0 ? messages : []
        const updated = updateWithDedup([userMessage, ...baseMessages])
        setMessages(updated)
      }
    } else {
      // We have a conversation ID - update both messages and current conversation state
      const currentConversation = appStateContext?.state.currentChat || 
                                   (localConversation && localConversation.id === conversationId ? localConversation : null);

      
      if (currentConversation && currentConversation.id === conversationId) {
        // Always use the current complete message state as the base
        const baseMessages = messages.length > 0 ? messages : (currentConversation.messages || [])
        const updatedMessages = updateWithDedup(baseMessages)
        const updatedConversation = { ...currentConversation, messages: updatedMessages }
        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: updatedConversation })
        setMessages(updatedMessages)
      } else {
        // Ensure we preserve all existing messages plus new user message
        const baseMessages = [...messages, userMessage]
        const updated = updateWithDedup(baseMessages)
        setMessages(updated)
      }
    }
    

  }

  /**
   * Clean streaming-only chat request handler (no CosmosDB persistence)
  /**
   * Clean streaming-only chat request handler
   * Replaces the old complex dual-path logic with simple SSE streaming
   */

  /**
   * Clean streaming-only chat request handler
   * Replaces the old complex dual-path logic with simple SSE streaming
   */
  const makeApiRequestWithCosmosDB = async (question: ChatMessage["content"], conversationId?: string) => {
    setIsLoading(true)
    setShowLoadingMessage(true)
    setStreamingContent('')
    setIsStreaming(false)
    setPendingClarification(null) // Clear any pending clarification
    
    const abortController = new AbortController()
    abortFuncs.current.unshift(abortController)
    
    // Prepare question content
    const questionContent = typeof question === 'string' 
      ? question 
      : [{ type: "text", text: (question[0] as any).text }, { type: "image_url", image_url: { url: (question[1] as any).image_url.url } }]
    
    // Clean display content (remove RESEARCH_CONTEXT_LINKS marker)
    let displayContent = typeof questionContent === 'string' ? questionContent : (question[0] as any)?.text || ''
    if (displayContent.includes('RESEARCH_CONTEXT_LINKS:')) {
      displayContent = displayContent.split('RESEARCH_CONTEXT_LINKS:')[0].trim()
    }

    const userMessage: ChatMessage = {
      id: uuid(),
      role: 'user',
      content: displayContent,
      date: new Date().toISOString()
    }

    // Build conversation context
    let conversation: Conversation | undefined
    if (conversationId) {
      const found = appStateContext?.state?.chatHistory?.find(conv => conv.id === conversationId)
      if (found?.messages?.length) {
        conversation = { ...found, messages: [...found.messages] }
      } else {
        // Load from backend if not in local state
        try {
          const historyResult = await historyRead(conversationId)
          conversation = {
            id: conversationId,
            title: displayContent.substring(0, 50) + '...',
            messages: historyResult?.messages || [],
            date: new Date().toISOString()
          }
          // Load pinned workspace if present
          if (historyResult?.pinnedWorkspaceId) {
            setPinnedWorkspaceId(historyResult.pinnedWorkspaceId);
          }
        } catch {
          conversation = { id: conversationId, title: 'Chat', messages: [], date: new Date().toISOString() }
        }
      }
      conversation.messages.push(userMessage)
      appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation })
    } else {
      setMessages(prev => [...prev, userMessage])
    }

    // Helper function to strip base64 image data from message content (prevents context overflow)
    const stripBase64FromContent = (content: string | any): string => {
      if (typeof content !== 'string') return typeof content === 'object' ? JSON.stringify(content) : String(content);
      // Replace base64 data URIs with placeholder, keeping the structure readable
      return content.replace(/!\[([^\]]*)\]\(data:image\/[^;]+;base64,[^)]+\)/g, '[Image: $1]');
    };

    // Build request
    const request: ConversationRequest & { model?: string; selected_data_source?: string; use_dynamic_system_message?: boolean; dalle_size?: string; dalle_style?: string; dalle_quality?: string; pinned_workspace_id?: string } = {
      messages: conversation 
        ? conversation.messages.filter(m => m.role !== ERROR).map(m => 
            m.id === userMessage.id 
              ? { ...m, content: questionContent as string } 
              : { ...m, content: stripBase64FromContent(m.content) }  // Strip base64 from history
          )
        : [{ id: userMessage.id, role: 'user', content: questionContent as string, date: userMessage.date }],
      ...(selectedModel && { model: selectedModel }),
      ...(selectedDataSource && { selected_data_source: selectedDataSource }),
      use_dynamic_system_message: userPreferences.use_dynamic_system_message ?? true,
      // DALL-E image generation options - only send if user explicitly set them
      ...(dalleSize && { dalle_size: dalleSize }),
      ...(dalleStyle && { dalle_style: dalleStyle }),
      ...(dalleQuality && { dalle_quality: dalleQuality }),
      // Pinned workspace for document editing
      ...(pinnedWorkspaceId && { pinned_workspace_id: pinnedWorkspaceId })
    }

    const documentRefs = sharedUploadedFiles.map(f => ({ 
      id: f.id, 
      filename: f.filename, 
      filetype: f.filename?.split('.').pop() || '',
      sheet_name: f.sheet_name // Selected sheet for Excel files
    }))
    let fullContent = ''
    let receivedConversationId: string | null = conversationId || null
    let receivedModel: string = selectedModel || 'gpt-4.1'
    let receivedSource: string = 'general_knowledge'
    let receivedIntentReasoning: any = null  // Store intent reasoning from backend

    // Show thinking bubble during loading
    showIntentReasoningDuringLoading({
      data_source: 'analyzing',
      reason: 'Analyzing your query to determine the best information source',
      confidence: 0,
      thinking: 'Analyzing your query'
    });

    try {
      setIsStreaming(true)
      
      await historyGenerate(
        request,
        abortController.signal,
        conversationId,
        documentRefs.length ? documentRefs : undefined,
        false, // webSearchMode
        {
          onMetadata: (metadata) => {
            receivedConversationId = metadata.conversation_id
            receivedModel = metadata.model || receivedModel
            // Don't hide loading message yet - wait for first token
            
            // Create new conversation if this is first message
            if (!conversationId && receivedConversationId) {
              conversationManager.updateConversationId(receivedConversationId)
              const newConv = {
                id: receivedConversationId,
                title: displayContent.substring(0, 50) + '...',
                messages: [userMessage],
                date: new Date().toISOString()
              }
              appStateContext?.dispatch({ type: 'UPDATE_CHAT_HISTORY', payload: newConv })
              appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: newConv })
              conversation = newConv
              
              // Save any uploaded documents to the new conversation
              if (sharedUploadedFiles && sharedUploadedFiles.length > 0) {
                (async () => {
                  try {
                    const { historyAddDocument } = await import('../../api');
                    for (const fileMeta of sharedUploadedFiles) {
                      await historyAddDocument(receivedConversationId, {
                        id: fileMeta.id || fileMeta.fileId,
                        fileId: fileMeta.fileId || fileMeta.id,
                        filename: fileMeta.filename || fileMeta.name,
                        type: fileMeta.type || 'document',
                        size: fileMeta.size || 0,
                        uploadDate: fileMeta.uploadDate || new Date().toISOString()
                      });
                      debugLog(`[Chat] Saved document ${fileMeta.filename} to new conversation ${receivedConversationId}`);
                    }
                  } catch (error) {
                    console.error('[Chat] Failed to save document references to new conversation:', error);
                  }
                })();
              }
            }
          },
          onIntentReasoning: (reasoning) => {
            // Store intent reasoning and update UI
            receivedIntentReasoning = reasoning
            receivedSource = reasoning.data_source || receivedSource
            showIntentReasoningDuringLoading(reasoning)
            
            // If we receive real intent (not 'analyzing') AND content has started streaming,
            // hide after user has time to see the source name (800ms)
            if (reasoning.data_source !== 'analyzing' && fullContent.length > 0) {
              setTimeout(() => hideLoadingMessage(), 800)
            }
          },
          onResearchPlan: (plan) => {
            // Show research plan prominently in thinking bubble
            const planText = plan.queries.map((q: string, i: number) => `  ${i + 1}. ${q}`).join('\n')
            showIntentReasoningDuringLoading({
              data_source: 'Deep Research',
              reason: `📋 Research Plan (${plan.total_queries} queries)`,
              confidence: 1.0,
              thinking: `📋 Research Plan:\n${planText}\n\n⏱️ Estimated: ~${Math.round(plan.estimated_time_seconds / 60)} minutes | 📚 ~${plan.estimated_sources} sources`
            })
          },
          onClarification: (clarification) => {
            // System needs user to select source/intent - show clarification cards
            console.log('[Chat] Clarification needed:', clarification);
            hideLoadingMessage();
            setPendingClarification(clarification);
            setIsLoading(false);
          },
          onWorkspaceRedirect: async (redirect) => {
            // Open TenderBuilder for document fill mode
            console.log('[Chat] Workspace redirect:', redirect);
            hideLoadingMessage();
            setIsLoading(false);
            setIsStreaming(false);
            
            // Fetch the workspace and open TenderBuilder panel
            if (redirect.workspace_id) {
              try {
                const workspace = await getWorkspace(redirect.workspace_id);
                if (workspace) {
                  console.log('[Chat] Opening TenderBuilder for workspace:', workspace.id);
                  setCurrentDraftId(workspace.id);
                  setCurrentWorkspace(workspace as TenderWorkspace);
                  setShowDocumentDraftPanel(true);
                  setIsDocumentPanelCollapsed(false);
                } else {
                  console.error('[Chat] Failed to fetch workspace for redirect');
                }
              } catch (error) {
                console.error('[Chat] Error fetching workspace for redirect:', error);
              }
            }
          },
          onToken: (token) => {
            // Hide loading message logic:
            // - If we have REAL intent reasoning (not 'analyzing'), show for 800ms so user sees source
            // - If still 'analyzing', keep showing until real intent arrives
            // - Fallback: hide after 2 seconds if no real intent (edge case)
            if (fullContent === '') {
              const hasRealIntent = receivedIntentReasoning && receivedIntentReasoning.data_source !== 'analyzing'
              if (hasRealIntent) {
                // Real intent received - give user time to see the source name
                setTimeout(() => hideLoadingMessage(), 800)
              } else {
                // Still 'analyzing' - don't hide yet, wait for real intent
                // Fallback timeout in case intent never arrives
                setTimeout(() => {
                  if (!receivedIntentReasoning || receivedIntentReasoning.data_source === 'analyzing') {
                    hideLoadingMessage()
                  }
                }, 2000)
              }
            }
            fullContent += token
            setStreamingContent(fullContent)
          },
          onDone: (data) => {
            // Store source from done event (prefer from intent_reasoning if available)
            receivedSource = data.source || receivedSource
            // Update intent reasoning from done event if present
            if (data.intent_reasoning) {
              receivedIntentReasoning = data.intent_reasoning
            }
            
            // Create assistant message with model and source info
            const assistantMsg: ChatMessage = {
              id: uuid(),
              role: ASSISTANT,
              content: fullContent,
              date: new Date().toISOString()
            }
            // Attach metadata to message
            ;(assistantMsg as any).model = receivedModel
            ;(assistantMsg as any).source = receivedSource
            // Track if response came from FAQ cache
            ;(assistantMsg as any).isCached = data.is_cached || receivedSource?.startsWith('faq_cache:')
            if (data.citations?.length) (assistantMsg as any).citations = data.citations
            
            // Attach Excel chart data and dashboard data from streaming response
            if (data.chart_data) {
              ;(assistantMsg as any).generated_chart = data.chart_data
            }
            // Support generated documents from streaming response
            if (data.generated_document) {
              ;(assistantMsg as any).generated_document = data.generated_document
              console.log('📄 Attached generated document to response:', data.generated_document)
              
              // Check if this is a workspace-based document (new flow)
              const workspaceId = data.workspace_id || data.generated_document.workspace_id
              const isContentAdded = data.action === 'content_added' || data.generated_document.action === 'content_added'
              
              if (workspaceId) {
                setCurrentDraftId(workspaceId)
                setDraftRefreshKey(prev => prev + 1)  // Trigger refresh if panel already open
                
                if (isContentAdded && isDocumentPanelCollapsed) {
                  // Content was added to pinned doc - show update indicator, don't expand
                  console.log('📥 Content added to document - showing update indicator on collapsed panel')
                  setHasDocumentUpdate(true)
                  setShowDocumentDraftPanel(true)  // Ensure panel is visible (even if collapsed)
                } else {
                  // New document or not collapsed - open panel normally
                  console.log('📝 Opening workspace-based document panel:', workspaceId)
                  setShowDocumentDraftPanel(true)
                  if (!isContentAdded) {
                    setIsDocumentPanelCollapsed(false)  // Only auto-expand for new docs, not for content additions
                  }
                }
              } else if (data.generated_document.document_url && 
                         data.generated_document.document_id && 
                         data.generated_document.filename && 
                         data.generated_document.format) {
                // Legacy flow: standalone document blob (only if all required fields present)
                setDocumentGenerationResult({
                  document_url: data.generated_document.document_url,
                  document_id: data.generated_document.document_id,
                  filename: data.generated_document.filename,
                  format: data.generated_document.format
                })
                setShowDocumentGenerationPanel(true)
              }
            }
            // Support multiple charts
            if (data.chart_specs && Array.isArray(data.chart_specs)) {
              // Store chart_specs in document_metadata for Answer component to access
              if (!(assistantMsg as any).document_metadata) {
                ;(assistantMsg as any).document_metadata = [{}]
              }
              ;(assistantMsg as any).document_metadata[0].chart_specs = data.chart_specs
              console.log(`📊 Attached ${data.chart_specs.length} charts to response`)
            }
            if (data.dashboard_data) {
              ;(assistantMsg as any).generated_dashboard = data.dashboard_data
            }
            
            // Handle multi-sheet Excel info - update uploaded files with available sheets
            if (data.available_sheets && data.available_sheets.length > 0) {
              console.log(`📊 Excel file has ${data.available_sheets.length} sheets, current: ${data.sheet_name}`)
              // Update the uploaded files state with sheet info
              setSharedUploadedFiles(prev => prev.map(f => {
                if (f.filename?.toLowerCase().endsWith('.xlsx') || f.filename?.toLowerCase().endsWith('.xls')) {
                  return {
                    ...f,
                    available_sheets: data.available_sheets,
                    sheet_name: f.sheet_name || data.sheet_name // Keep user selection or use current
                  };
                }
                return f;
              }));
            }
            
            // Attach intent_reasoning for the info icon display
            if (receivedIntentReasoning) {
              ;(assistantMsg as any).intent_reasoning = receivedIntentReasoning
              // Also set sourceConfidence from intent reasoning
              if (receivedIntentReasoning.confidence !== undefined) {
                ;(assistantMsg as any).sourceConfidence = receivedIntentReasoning.confidence
              }
            }

            // Update conversation
            const finalConvId = receivedConversationId || conversationId
            if (conversation) {
              conversation.messages.push(assistantMsg)
              // Update both currentChat AND chatHistory for proper state sync
              appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation })
              appStateContext?.dispatch({ type: 'UPDATE_CHAT_HISTORY', payload: conversation })
              setMessages([...conversation.messages])
            } else if (finalConvId) {
              const conv = appStateContext?.state?.chatHistory?.find(c => c.id === finalConvId)
              if (conv) {
                conv.messages.push(assistantMsg)
                appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conv })
                appStateContext?.dispatch({ type: 'UPDATE_CHAT_HISTORY', payload: conv })
                setMessages([...conv.messages])
              }
            } else {
              setMessages(prev => [...prev, assistantMsg])
            }
            
            setIsStreaming(false)
            setStreamingContent('')
          },
          onError: (error) => {
            console.error('[Chat] Streaming error:', error)
            
            // Check for token limit exceeded error
            if (error.startsWith('TOKEN_LIMIT_EXCEEDED:')) {
              const parts = error.split(':');
              const blockedBy = parts[1] || 'unknown';
              const reason = parts.slice(2).join(':') || 'You have reached your token usage limit.';
              setTokenLimitError({ blockedBy, reason });
              setShowTokenLimitDialog(true);
              setIsLoading(false);
              setShowLoadingMessage(false);
              setIsStreaming(false);
              setStreamingContent('');
              return;
            }
            
            const errorMsg: ChatMessage = { id: uuid(), role: ERROR, content: error, date: new Date().toISOString() }
            if (conversation) {
              conversation.messages.push(errorMsg)
              setMessages([...conversation.messages])
            } else {
              setMessages(prev => [...prev, errorMsg])
            }
            setIsStreaming(false)
            setStreamingContent('')
          }
        }
      )
    } catch (e: any) {
      if (!abortController.signal.aborted) {
        const errorMsg: ChatMessage = { id: uuid(), role: ERROR, content: e.message || 'Request failed', date: new Date().toISOString() }
        setMessages(prev => [...prev, errorMsg])
      }
    } finally {
      setIsLoading(false)
      setShowLoadingMessage(false)
      setIsStreaming(false)
      abortFuncs.current = abortFuncs.current.filter(a => a !== abortController)
      setProcessMessages(messageStatus.Done)
    }
  }

  const clearChat = () => {
    setMessages([]); // This clears all chat messages
    clearDocumentContext(); // Use our helper to ensure all document state is reset consistently
  }

  const tryGetRaiPrettyError = (errorMessage: string) => {
    try {
      // Using a regex to extract the JSON part that contains "innererror"
      const match = errorMessage.match(/'innererror': ({.*})\}\}/)
      if (match) {
        // Replacing single quotes with double quotes and converting Python-like booleans to JSON booleans
        const fixedJson = match[1]
          .replace(/'/g, '"')
          .replace(/\bTrue\b/g, 'true')
          .replace(/\bFalse\b/g, 'false')
        const innerErrorJson = JSON.parse(fixedJson)
        let reason = ''
        // Check if jailbreak content filter is the reason of the error
        const jailbreak = innerErrorJson.content_filter_result.jailbreak
        if (jailbreak.filtered === true) {
          reason = 'Jailbreak'
        }

        // Returning the prettified error message
        if (reason !== '') {
          return (
            'The prompt was filtered due to triggering Azure OpenAI\'s content filtering system.\n' +
            'Reason: This prompt contains content flagged as ' +
            reason +
            '\n\n' +
            'Please modify your prompt and retry. Learn more: https://go.microsoft.com/fwlink/?linkid=2198766'
          )
        }
      }
    } catch (e) {
      console.error('Failed to parse the error:', e)
    }
    return errorMessage
  }

  const parseErrorMessage = (errorMessage: string) => {
    let errorCodeMessage = errorMessage.substring(0, errorMessage.indexOf('-') + 1)
    const innerErrorCue = "{\\'error\\': {\\'message\\': "
    if (errorMessage.includes(innerErrorCue)) {
      try {
        let innerErrorString = errorMessage.substring(errorMessage.indexOf(innerErrorCue))
        if (innerErrorString.endsWith("'}}")) {
          innerErrorString = innerErrorString.substring(0, innerErrorString.length - 3)
        }
        innerErrorString = innerErrorString.replaceAll("\\'", "'")
        let newErrorMessage = errorCodeMessage + ' ' + innerErrorString
        errorMessage = newErrorMessage
      } catch (e) {
        console.error('Error parsing inner error message: ', e)
      }
    }

    return tryGetRaiPrettyError(errorMessage)
  }

  const newChat = () => {
    setProcessMessages(messageStatus.Processing)
    setMessages([])
    setIsCitationPanelOpen(false)
    setIsIntentsPanelOpen(false)
    setActiveCitation(undefined)
    
    // Clear document context using our helper
    clearDocumentContext()
    
    // 🎯 Reset conversation manager for new conversation
    conversationManager.startNewConversation()
    
    // Update global state
    appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: null })
    setProcessMessages(messageStatus.Done)
  }

  const stopGenerating = () => {
    abortFuncs.current.forEach(a => a.abort())
    setShowLoadingMessage(false)
    setIsLoading(false)
  }

  useEffect(() => {
    if (appStateContext?.state.currentChat) {
      setMessages(appStateContext.state.currentChat.messages)
    } else {
      setMessages([])
    }
  }, [appStateContext?.state.currentChat])

  useLayoutEffect(() => {
    // 🎯 SIMPLIFIED HISTORY MANAGEMENT
    // With unified history service, messages are saved automatically during conversation.
    // No need for complex manual saving, deduplication, or error handling.
    
    if (appStateContext && appStateContext.state.currentChat && processMessages === messageStatus.Done) {
      // Simply ensure UI state is consistent - no database operations needed
      setProcessMessages(messageStatus.NotRunning)
    }
  }, [processMessages])

  useEffect(() => {
    if (AUTH_ENABLED !== undefined) getUserInfoList()
  }, [AUTH_ENABLED])

  useLayoutEffect(() => {
    chatMessageStreamEnd.current?.scrollIntoView({ behavior: 'smooth' })
  }, [showLoadingMessage, processMessages])

  const onShowCitation = (citation: Citation) => {
    setActiveCitation(citation)
    setIsCitationPanelOpen(true)
  }

  const onShowExecResult = (answerId: string) => {
    setIsIntentsPanelOpen(true)
  }

  const onViewSource = (citation: Citation) => {
    // Allow any URL that looks like a valid web link
    // Only skip blob URLs that are internal paths without SAS tokens or proper access
    if (citation.url) {
      const url = citation.url;
      const isBlobUrl = url.includes('blob.core.windows.net');
      // Allow blob URLs that have SAS tokens (contain '?' with 'sig=') or are CDN/public URLs
      const hasSasToken = url.includes('?') && (url.includes('sig=') || url.includes('sv='));
      const isPublicBlobOrCdn = url.includes('.blob.core.windows.net') && url.startsWith('https://');
      
      if (!isBlobUrl || hasSasToken || isPublicBlobOrCdn) {
        window.open(url, '_blank')
      }
    }
  }

  const parseCitationFromMessage = (message: ChatMessage) => {
    if (message?.role && message?.role === 'tool' && typeof message?.content === "string") {
      try {
        const toolMessage = JSON.parse(message.content) as ToolMessageContent
        return toolMessage.citations
      } catch {
        return []
      }
    }
    return []
  }

  const parsePlotFromMessage = (message: ChatMessage) => {
    // Method 1: Check for generated_chart field directly in message
    if ((message as any)?.generated_chart) {
      try {
        const chartData = typeof (message as any).generated_chart === 'string' 
          ? JSON.parse((message as any).generated_chart) 
          : (message as any).generated_chart;
        return JSON.stringify(chartData);
      } catch (e) {
        console.error('Failed to parse generated_chart:', e);
      }
    }
    
    // Method 2: Check if this is a tool message with SQL execution results
    if (message?.role && message?.role === "tool" && typeof message?.content === "string") {
      try {
        const execResults = JSON.parse(message.content) as AzureSqlServerExecResults;
        const codeExecResult = execResults.all_exec_results.at(-1)?.code_exec_result;

        if (codeExecResult === undefined) {
          return null;
        }
        return codeExecResult.toString();
      }
      catch {
        return null;
      }
    }
    
    // Method 3: Check if this message has document metadata with chart data (Excel analysis)
    if ((message as any)?.document_metadata) {
      const docMeta = (message as any).document_metadata;
      
      for (const doc of docMeta) {
        if (doc.has_visualization && doc.chart_data) {
          // Return the chart specification for rendering
          return JSON.stringify(doc.chart_data);
        }
      }
    }
    
    return null;
  }

  const disabledButton = () => {
    return (
      isLoading ||
      (messages && messages.length === 0) ||
      clearingChat ||
      appStateContext?.state.chatHistoryLoadingState === ChatHistoryLoadingState.Loading
    )
  }

  // Generate images with Google Imagen when selected in the model picker
  const makeImagenRequest = async (questionText: string) => {
    setIsLoading(true);
    setShowLoadingMessage(true);
    
    // Detect Hebrew for thinking bubble
    const isHebrew = /[\u0590-\u05FF]/.test(questionText);
    
    // Show thinking bubble during image generation
    showIntentReasoningDuringLoading({
      data_source: 'Imagen / Image Generation',
      reason: isHebrew ? 'יוצר תמונה עם Imagen...' : 'Generating image with Imagen...',
      confidence: 1.0,
      thinking: isHebrew ? `יוצר תמונה: "${questionText.substring(0, 50)}${questionText.length > 50 ? '...' : ''}"` : `Generating: "${questionText.substring(0, 50)}${questionText.length > 50 ? '...' : ''}"`,
    });

    const userMessage: ChatMessage = {
      id: uuid(),
      role: 'user',
      content: questionText,
      date: new Date().toISOString(),
    };

    try {
      // Update current conversation locally
      let currentConversation = appStateContext?.state.currentChat;
      if (!currentConversation) {
        currentConversation = {
          id: uuid(),
          title: questionText.substring(0, 50) + (questionText.length > 50 ? '...' : ''),
          messages: [userMessage],
          date: new Date().toISOString(),
          fromBackend: false,
        } as Conversation;
      } else {
        currentConversation = {
          ...currentConversation,
          messages: [...currentConversation.messages, userMessage],
        };
      }
      appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: currentConversation });
      setMessages(currentConversation.messages);

      const body = {
        prompt: questionText,
        provider: 'imagen',  // Explicitly set provider for Imagen
        n: Math.max(1, Math.min(imagenCount || 1, 4)),
        size: imagenAspectRatio === '1:1' ? '1024x1024' : (imagenAspectRatio === '16:9' ? '1536x864' : (imagenAspectRatio === '9:16' ? '864x1536' : (imagenAspectRatio === '4:3' ? '1408x1056' : (imagenAspectRatio === '3:4' ? '1056x1408' : '1024x1024')))),
        aspect_ratio: imagenAspectRatio,
        ...(selectedModel ? { model: selectedModel } : {}),
      };

      const resp = await authenticatedFetch('/image/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await resp.json();

      const assistantMessage: ChatMessage & { images?: Array<{ b64: string; mime_type: string }> } = {
        id: uuid(),
        role: 'assistant',
        content: (data && data.error) ? `Image generation failed: ${data.error}` : 'Here are your generated images:',
        date: new Date().toISOString(),
        ...(Array.isArray(data?.images) ? { images: data.images } : {}),
      };

      const updatedMessages = [...(currentConversation.messages || []), assistantMessage];
      const updatedConversation = { ...currentConversation, messages: updatedMessages };
      appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: updatedConversation });
      setMessages(updatedMessages);
    } catch (err: any) {
      const assistantMessage: ChatMessage = {
        id: uuid(),
        role: 'assistant',
        content: `Image generation error: ${err?.message || 'Unknown error'}`,
        date: new Date().toISOString(),
      };
      setMessages(prev => [...prev, assistantMessage]);
    } finally {
      setIsLoading(false);
      setShowLoadingMessage(false);
    }
  };

  // Generate images with GPT Image 1.5 when selected in the model picker
  const makeDalleRequest = async (questionText: string) => {
    setIsLoading(true);
    setShowLoadingMessage(true);
    
    // Detect Hebrew for UI messages
    const isHebrew = /[\u0590-\u05FF]/.test(questionText);
    
    // Show thinking bubble during image generation
    showIntentReasoningDuringLoading({
      data_source: 'Image Generation',
      reason: isHebrew ? 'יוצר תמונה...' : 'Generating image...',
      confidence: 1.0,
      thinking: isHebrew ? `יוצר תמונה: "${questionText.substring(0, 50)}${questionText.length > 50 ? '...' : ''}"` : `Generating: "${questionText.substring(0, 50)}${questionText.length > 50 ? '...' : ''}"`,
    });

    const userMessage: ChatMessage = {
      id: uuid(),
      role: 'user',
      content: questionText,
      date: new Date().toISOString(),
    };

    try {
      // Update current conversation locally
      let currentConversation = appStateContext?.state.currentChat;
      if (!currentConversation) {
        currentConversation = {
          id: uuid(),
          title: questionText.substring(0, 50) + (questionText.length > 50 ? '...' : ''),
          messages: [userMessage],
          date: new Date().toISOString(),
          fromBackend: false,
        } as Conversation;
      } else {
        currentConversation = {
          ...currentConversation,
          messages: [...currentConversation.messages, userMessage],
        };
      }
      appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: currentConversation });
      setMessages(currentConversation.messages);

      const body = {
        prompt: questionText,
        provider: 'gpt-image',
        // Only include options if user explicitly set them
        ...(dalleSize && { size: dalleSize }),
        ...(dalleQuality && { quality: dalleQuality }),
      };

      const resp = await authenticatedFetch('/image/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await resp.json();

      const successMsg = isHebrew ? 'הנה התמונות שיצרתי:' : 'Here are your generated images:';
      const errorPrefix = isHebrew ? 'יצירת תמונה נכשלה:' : 'Image generation failed:';

      const assistantMessage: ChatMessage & { images?: Array<{ b64: string; mime_type: string; revised_prompt?: string }> } = {
        id: uuid(),
        role: 'assistant',
        content: (data && data.error) ? `${errorPrefix} ${data.error}` : successMsg,
        date: new Date().toISOString(),
        ...(Array.isArray(data?.images) ? { images: data.images } : {}),
      };

      const updatedMessages = [...(currentConversation.messages || []), assistantMessage];
      const updatedConversation = { ...currentConversation, messages: updatedMessages };
      appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: updatedConversation });
      setMessages(updatedMessages);
    } catch (err: any) {
      const errorMsg = isHebrew ? `שגיאה ביצירת תמונה: ${err?.message || 'שגיאה לא ידועה'}` : `Image generation error: ${err?.message || 'Unknown error'}`;
      const assistantMessage: ChatMessage = {
        id: uuid(),
        role: 'assistant',
        content: errorMsg,
        date: new Date().toISOString(),
      };
      setMessages(prev => [...prev, assistantMessage]);
    } finally {
      setIsLoading(false);
      setShowLoadingMessage(false);
    }
  };


  // This function sends a question to the simple-chat endpoint with document context
  const makeDocumentChatRequest = async (question: string) => {
    setIsLoading(true);
    setShowLoadingMessage(true);
    
    // 🎯 Show "Analyzing document" message during document processing
    showIntentReasoningDuringLoading({
      data_source: 'document_analysis',
      reason: 'Analyzing uploaded documents to provide context-aware responses',
      confidence: 1.0,
      thinking: 'Analyzing document content'
    });
    
    // 🎯 CRITICAL FIX: Use existing conversation from app state to maintain continuity
    const existingConversation = appStateContext?.state.currentChat;
    const existingConversationId = existingConversation?.id;
    
    console.log('[Chat] makeDocumentChatRequest - existing conversation ID:', existingConversationId);
    console.log('[Chat] makeDocumentChatRequest - documents available:', sharedUploadedFiles?.length || 0);
    console.log('[Chat] makeDocumentChatRequest - app state currentChat:', appStateContext?.state.currentChat);
    
    // Create user message for UI display
    const userMessage: ChatMessage = {
      id: uuid(),
      role: "user",
      content: question,
      date: new Date().toISOString()
    };

    try {
      // 🎯 UNIFIED CONVERSATION MANAGEMENT for Document Chat
      // Use existing conversation if available, otherwise create new one
      let currentConversation;
      
      if (existingConversationId) {
        // Continue existing conversation
        currentConversation = {
          ...existingConversation,
          messages: [...(existingConversation.messages || []), userMessage],
          isPdfChat: true,
          fromBackend: true
        };
        console.log('[Chat] Continuing existing conversation:', existingConversationId);
      } else {
        // Create new conversation only if no existing one
        currentConversation = conversationManager.getCurrentOrCreateConversation(userMessage, question);
        currentConversation = {
          ...currentConversation,
          isPdfChat: true,
          fromBackend: false
        };
        console.log('[Chat] Created new conversation for documents');
      }

      // Update both local state and global state
      appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: currentConversation });
      setMessages(currentConversation.messages);
      
      // Ensure we're using the correct document context - retrieve documents if needed
      if (currentConversation.id && (!sharedUploadedFiles || sharedUploadedFiles.length === 0)) {
        try {
          const documentRefs = await historyGetDocuments(currentConversation.id);
          if (documentRefs && documentRefs.length > 0) {
            setSharedUploadedFiles(transformBackendDocuments(documentRefs));
          }
        } catch (error) {
          console.error("Error retrieving document references:", error);
        }
      }
      
      // Check if we have uploaded files
      if (!sharedUploadedFiles || sharedUploadedFiles.length === 0) {
        // If we're in a conversation with documents, try to get the documents from history
        if (currentConversation?.id) {
          try {
            const documentRefs = await historyGetDocuments(currentConversation.id);
            if (documentRefs && documentRefs.length > 0) {
              // We have documents in the conversation history, use those
              setSharedUploadedFiles(transformBackendDocuments(documentRefs));
            } else {
              const errorMessage: ChatMessage = {
                id: uuid(),
                role: "assistant",
                content: "Error: No files uploaded. Please upload files and try again.",
                date: new Date().toISOString()
              };
              
              currentConversation = {
                ...currentConversation,
                messages: [...currentConversation.messages, errorMessage]
              };
              
              appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: currentConversation });
              setMessages(currentConversation.messages);
              setIsLoading(false);
              setShowLoadingMessage(false);
              return;
            }
          } catch (error) {
            console.error("Error fetching document references:", error);
            const errorMessage: ChatMessage = {
              id: uuid(),
              role: "assistant",
              content: "Error: Could not retrieve document information. Please try again.",
              date: new Date().toISOString()
            };
            
            currentConversation = {
              ...currentConversation,
              messages: [...currentConversation.messages, errorMessage]
            };
            
            appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: currentConversation });
            setMessages(currentConversation.messages);
            setIsLoading(false);
            setShowLoadingMessage(false);
            return;
          }
        } else {
          const errorMessage: ChatMessage = {
            id: uuid(),
            role: "assistant",
            content: "Error: No files uploaded. Please upload files and try again.",
            date: new Date().toISOString()
          };
          
          currentConversation = {
            ...currentConversation,
            messages: [...currentConversation.messages, errorMessage]
          };
          
          appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: currentConversation });
          setMessages(currentConversation.messages);
          setIsLoading(false);
          setShowLoadingMessage(false);
          return;
        }
      }

      // Extract all file IDs from uploaded files
      const file_ids = sharedUploadedFiles.map(file => file.id).filter(id => id);
      if (file_ids.length === 0) {
        const errorMessage: ChatMessage = {
          id: uuid(),
          role: "assistant",
          content: "Error: No valid file IDs found. Please upload files and try again.",
          date: new Date().toISOString()
        };
        
        currentConversation = {
          ...currentConversation,
          messages: [...currentConversation.messages, errorMessage]
        };
        
        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: currentConversation });
        setMessages(currentConversation.messages);
        setIsLoading(false);
        setShowLoadingMessage(false);
        return;
      }

      // Ensure conversation is created in history first if it's new
      // We only want to create a new conversation once for each document chat session
      // Check if this conversation has never been saved to the backend
      // If it has a UUID generated by the frontend but hasn't been saved to the backend yet
      const needsBackendCreation = currentConversation && 
          !currentConversation.id.startsWith('history-') &&  // Not from history API
          currentConversation.id.includes('-') &&           // Is a UUID format
          !currentConversation.fromBackend;                 // Not already saved
          
      console.log("[Chat] Checking if conversation needs backend creation:", 
          { id: currentConversation?.id, needsBackendCreation });
          
      if (needsBackendCreation) {
        console.log("[Chat] Creating new conversation in history before document chat", currentConversation);
        
        try {
          // Create conversation in history first
          const historyResponse = await authenticatedFetch("/history/create", {
            method: "POST",
            body: JSON.stringify({
              title: currentConversation.title || "Document Conversation",
              messages: [],
              // Include document references directly in conversation creation
              documents: file_ids.map(id => {
                // Find the file metadata from sharedUploadedFiles
                const fileInfo = sharedUploadedFiles.find(f => f.id === id);
                return {
                  id: id,
                  filename: fileInfo?.filename || "Document",
                  filetype: fileInfo?.file?.type || fileInfo?.type === 'document' ? "application/pdf" : "application/octet-stream",
                  timestamp: new Date().toISOString()
                };
              })
            }),
          });
          
          if (historyResponse.ok) {
            const historyData = await historyResponse.json();
            const historyId = historyData.id;
            
            // Update conversation with history ID
            const updatedConversation: Conversation = {
              ...currentConversation,
              id: historyId,
              fromBackend: true,  // Mark that this conversation is from the backend
              // Add document references to the current conversation in memory
              documents: file_ids.map(id => {
                const fileInfo = sharedUploadedFiles.find(f => f.id === id);
                return {
                  id: id,
                  filename: fileInfo?.filename || "Document",
                  filetype: fileInfo?.file?.type || fileInfo?.type === 'document' ? "application/pdf" : "application/octet-stream",
                  timestamp: new Date().toISOString()
                };
              })
            };
            
            // Update the currentConversation with our updated version
            currentConversation = updatedConversation;
            
            // Update global state with the conversation that includes document references
            if (appStateContext) {
              appStateContext.dispatch({ 
                type: 'UPDATE_CURRENT_CHAT', 
                payload: currentConversation 
              });
            }
            
            console.log("[Chat] Created conversation in history with ID:", historyId);
            
            // Add a small delay to ensure the document association is complete
            await new Promise(resolve => setTimeout(resolve, 500));
          } else {
            console.error("[Chat] Failed to create conversation in history:", await historyResponse.text());
          }
        } catch (historyError) {
          console.error("[Chat] Error creating conversation in history:", historyError);
        }
      }
      
      // Add a flag to track if this is a PDF chat conversation
      if (!currentConversation.isPdfChat && file_ids.length > 0) {
        currentConversation = {
          ...currentConversation,
          isPdfChat: true
        };
      }
      
      // Check if we have Excel files and if this is a visualization request
      const hasExcelFiles = sharedUploadedFiles.some(file => 
        file.filename && file.filename.toLowerCase().endsWith('.xlsx')
      );
      
      // Check if this is a visualization request
      const visualizationKeywords = [
        'create a chart', 'create a graph', 'create bar chart', 'create line chart', 
        'create pie chart', 'bar chart', 'line chart', 'pie chart', 'chart',
        'graph', 'plot', 'visualize', 'visualization', 'performance'
      ];
      
      const isVisualizationRequest = visualizationKeywords.some(keyword => 
        question.toLowerCase().includes(keyword.toLowerCase())
      );
      
      console.log("[Chat] Excel routing check:", {
        hasExcelFiles,
        isVisualizationRequest, 
        question: question.substring(0, 50)
      });
      
      if (currentConversation) {
        // Make VERY SURE we're using the backend conversation ID
        // This is critical - if we don't use a valid backend ID, a new conversation will be created
        const conversationId = currentConversation.id;
        
        let response;
        
        // 🎯 NEW: Use simple-chat endpoint for ALL document requests instead of separate endpoints
        console.log("[Chat] Using simple-chat endpoint for document request:", {
          conversationId,
          fromBackend: currentConversation.fromBackend,
          isPdfChat: currentConversation.isPdfChat,
          fileCount: file_ids.length,
          hasExcelFiles,
          isVisualizationRequest
        });
        

        
        // Prepare document references for simple chat
        const documentRefs = sharedUploadedFiles.map(file => ({
          id: file.id,
          filename: file.filename,
          filetype: file.filename?.split('.').pop() || ''
        }));
        
        // Prepare messages for simple chat (include conversation history if available)
        let messages: any[] = [{ 
          id: userMessage.id,
          role: 'user', 
          content: question, 
          date: userMessage.date 
        }];
        
        // If we have an existing conversation, include the history
        if (currentConversation && currentConversation.messages && currentConversation.messages.length > 1) {
          // Include all previous messages except the current user message we just added
          const previousMessages = currentConversation.messages.slice(0, -1).filter(msg => msg.role !== 'error');
          const convertedMessages = previousMessages.map(msg => ({
            id: msg.id,
            role: msg.role,
            content: typeof msg.content === 'string' ? msg.content : msg.content[0]?.text || '',
            date: msg.date
          }));
          messages = [...convertedMessages, ...messages];
        }
        
        // 🎯 FIX: Add explicit context about multiple documents being separate files
        const enhancedMessages = [...messages];
        if (documentRefs.length > 1 && enhancedMessages.length > 0) {
          const lastMessage = enhancedMessages[enhancedMessages.length - 1];
          if (lastMessage.role === 'user') {
            lastMessage.content = `${lastMessage.content}\n\nNote: I have uploaded ${documentRefs.length} separate documents: ${documentRefs.map(doc => `"${doc.filename}"`).join(', ')}. Please analyze each document separately and refer to them by their individual filenames, not as sections of a single document.`;
          }
        }
        
        // Use optimized API client for chat requests
        try {
          const chatResponse = await apiClient.sendChatMessage({ 
            messages: enhancedMessages,
            // 🎯 CRITICAL FIX: Convert empty string to undefined for backend
            conversation_id: conversationId || undefined,
            documents: documentRefs,  // Include document references
            model: selectedModel || undefined  // Include selected model
          });
          
          if (chatResponse.error) {
            throw new Error(chatResponse.error);
          }
          
          // Handle response the same way as regular chat
          const responseConversationId = chatResponse.history_metadata?.conversation_id || conversationId;
          const msgs = chatResponse.choices?.[0]?.messages || [];
          
          // 🎯 NEW: Handle document removal notification
          if (chatResponse.documents_removed) {
            console.log('[Chat] Documents were auto-removed:', chatResponse.documents_removed);
            setDocumentRemovalNotification(chatResponse.documents_removed);
            
            // Clear local document state
            setSharedUploadedFiles([]);
            setPdfUploaded(false);
            
            // Auto-hide notification after 8 seconds
            setTimeout(() => {
              setDocumentRemovalNotification(null);
            }, 8000);
          }
          
          // 🎯 CRITICAL FIX: Update conversation ID if we got one from backend
          if (responseConversationId && responseConversationId !== conversationId) {
            currentConversation.id = responseConversationId;
            console.log('[Chat] Updated conversation ID from backend:', responseConversationId);
            
            // Update app state with the new conversation ID
            appStateContext?.dispatch({ 
              type: 'UPDATE_CURRENT_CHAT', 
              payload: { ...currentConversation, id: responseConversationId }
            });
          }
          
          if (Array.isArray(msgs) && msgs.length > 0) {
            setShowLoadingMessage(false);

            
            msgs.forEach(msg => {
              msg.id = chatResponse.id || uuid();
              msg.date = new Date().toISOString();
              if (msg.role === 'assistant' && chatResponse.model) {
                (msg as any).model = chatResponse.model;
              }
              // Add document metadata if available
              if (chatResponse.document_metadata && chatResponse.document_metadata.length > 0) {
                (msg as any).document_metadata = chatResponse.document_metadata;
              }
              // Add generated document info for document generation panel
              if (chatResponse.generated_document) {
                (msg as any).generated_document = chatResponse.generated_document;
              }
              // Pass source information from the API response
              processResultMessage(msg, userMessage, responseConversationId, currentConversation, chatResponse.source, chatResponse.sourceConfidence);
            });
          } else {
            throw new Error('No response content received');
          }
        } catch (e) {
          const errMsg = (e as Error)?.message ? `Error communicating with AI service: ${(e as Error).message}` : 'Error communicating with AI service.';
          const errorMessage: ChatMessage = {
            id: uuid(),
            role: "assistant",
            content: errMsg,
            date: new Date().toISOString()
          };
          
          let currentConversation = appStateContext?.state.currentChat;
          if (currentConversation) {
            currentConversation = {
              ...currentConversation,
              messages: [...currentConversation.messages, errorMessage]
            };
            
            appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: currentConversation });
            setMessages(currentConversation.messages);
          }
        } finally {
          setIsLoading(false);
          setShowLoadingMessage(false);
          setProcessMessages(messageStatus.Done);
        }
      }
    } catch (overallError) {
      console.error('[Chat] Overall document chat error:', overallError);
      
      const errorMessage: ChatMessage = {
        id: uuid(),
        role: "assistant", 
        content: `Error processing request: ${overallError instanceof Error ? overallError.message : 'Unknown error'}`,
        date: new Date().toISOString()
      };
      
      let currentConversation = appStateContext?.state.currentChat;
      if (currentConversation) {
        currentConversation = {
          ...currentConversation,
          messages: [...currentConversation.messages, errorMessage]
        };
        
        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: currentConversation });
        setMessages(currentConversation.messages);
      }
      
      setIsLoading(false);
      setShowLoadingMessage(false);
      setProcessMessages(messageStatus.Done);
    }
  };

  // Handler for new upload flow
  const handleUploadSuccess = async (message: string, fileMetas: any[]) => {
    setUploadedFileId(fileMetas[0]?.id);
    setFilePreview(fileMetas[0]?.preview || '');
    setAnswer('');
    setQuestion('');
    // Store all uploaded files to be used when creating/updating conversations
    setSharedUploadedFiles(prev => [...prev, ...fileMetas]);
    setPdfUploaded(true);
    
    // Save document references to the conversation in Cosmos
    const currentConversation = appStateContext?.state.currentChat;
    if (currentConversation?.id) {
      try {
        const { historyAddDocument } = await import('../../api');
        for (const fileMeta of fileMetas) {
          await historyAddDocument(currentConversation.id, {
            id: fileMeta.id || fileMeta.fileId,
            fileId: fileMeta.fileId || fileMeta.id,
            filename: fileMeta.filename || fileMeta.name,
            type: fileMeta.type || 'document',
            size: fileMeta.size || 0,
            uploadDate: new Date().toISOString()
          });
          debugLog(`[Chat] Saved document ${fileMeta.filename} to conversation ${currentConversation.id}`);
        }
      } catch (error) {
        console.error('[Chat] Failed to save document references:', error);
      }
    }
  };

  // Handle file removal from QuestionInput - now using sharedUploadedFiles
  // Also calls backend to cancel any ongoing processing (prevents memory issues)
  const handleFileRemoved = async (fileId: string) => {
    console.log("[Chat] 🗑️ Removing file:", fileId);
    
    // 1. FIRST: Signal backend to STOP processing (critical for large files)
    try {
      await cancelUpload(fileId);
    } catch (error) {
      console.warn("[Chat] Cancel upload error (non-blocking):", error);
    }
    
    // 2. Remove from local polling/tracking
    removeUpload(fileId);
    
    // 3. Update local state (responsive UI)
    setSharedUploadedFiles(prev => {
      const filteredFiles = prev.filter(file => file.id !== fileId);
      if (filteredFiles.length === 0) {
        setPdfUploaded(false);
      }
      return filteredFiles;
    });
    
    // 4. Delete the file from storage (cleanup)
    try {
      await deleteExistingUpload(fileId);
      console.log("[Chat] ✅ File cleanup complete:", fileId);
    } catch (error) {
      console.error("[Chat] ❌ Error deleting file (non-blocking):", error);
    }
  };

  // Handle PDF to Word conversion directly (no popup)
  const handlePdfToWordConversion = async (pdfFile: any) => {
    const filename = pdfFile.filename || pdfFile.name || 'document.pdf';
    const fileId = pdfFile.id || pdfFile.fileId;
    console.log('[Chat] Starting PDF to Word conversion for:', filename, 'fileId:', fileId);
    
    // Show converting notification
    setPdfConversionNotification({
      status: 'converting',
      filename: filename
    });
    
    try {
      let response;
      
      if (fileId) {
        // Use file_id to convert from blob storage (preferred)
        console.log('[Chat] Converting via file_id:', fileId);
        response = await authenticatedFetch('/api/translation/convert-to-word', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            file_id: fileId,
            mode: 'semantic'
          })
        });
      } else if (pdfFile.file) {
        // Fallback: Direct file upload (if file object available)
        console.log('[Chat] Converting via direct upload');
        const formData = new FormData();
        formData.append('file', pdfFile.file);
        response = await authenticatedFetch('/api/translation/convert-to-word', {
          method: 'POST',
          body: formData
        });
      } else {
        // No file_id and no file object
        throw new Error('PDF file not available for conversion. Please re-upload the file.');
      }
      
      const data = await response.json();
      
      if (data.success && data.word_document_url) {
        console.log('[Chat] PDF conversion successful:', data.word_filename);
        setPdfConversionNotification({
          status: 'success',
          filename: data.word_filename,
          downloadUrl: data.word_document_url
        });
        
        // Auto-hide after 30 seconds
        setTimeout(() => {
          setPdfConversionNotification(null);
        }, 30000);
      } else {
        throw new Error(data.error || 'Conversion failed');
      }
    } catch (err: any) {
      console.error('[Chat] PDF conversion error:', err);
      setPdfConversionNotification({
        status: 'error',
        filename: filename,
        error: err.message || 'Conversion failed'
      });
      
      // Auto-hide error after 10 seconds
      setTimeout(() => {
        setPdfConversionNotification(null);
      }, 10000);
    }
  };

  // Document Manager specific handlers
  const handleDocumentManagerAdd = () => {
    // Trigger our direct file input (same as drag and drop)
    fileInputRef.current?.click();
  };

  // Handle translate file selection - direct to translation, no analysis
  const handleTranslateFileInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const fileArray = Array.from(files);
      const filenames = fileArray.map(f => f.name);
      console.log(`[Chat] Files selected for translation: ${filenames.join(', ')}`);
      
      // Create file objects for translation panel
      const translationFiles = fileArray.map((file, index) => ({
        id: `translate-${Date.now()}-${index}`,
        name: file.name,
        filename: file.name,
        file: file,  // Include the actual File object for direct upload
        type: file.type,
        size: file.size
      }));
      
      // Set files for translation and show translation panel
      setSharedUploadedFiles(translationFiles);
      setShowTranslationPanel(true);
      setTranslationPanelMessage('translate these documents');
    }
    // Clear the input so the same file can be selected again
    event.target.value = '';
  };

  // Handle file selection from our direct file input
  const handleFileInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    // Check if file upload is disabled
    if (ui?.show_upload_button === false) {
      console.log('[Chat] File upload is disabled via UI settings');
      event.target.value = ''; // Clear the input
      return;
    }
    
    const files = event.target.files;
    if (files && files.length > 0) {
      const fileArray = Array.from(files);
      const filenames = fileArray.map(f => f.name);
      console.log(`[Chat] Files selected via upload button: ${filenames.join(', ')}`);
      
      // Check for duplicates first
      console.log('[Chat] Checking for duplicate files...');
      setIsCheckingDuplicates(true);
      const duplicateResult = await checkDuplicates(filenames, 14);
      setIsCheckingDuplicates(false);
      console.log('[Chat] Duplicate check result:', duplicateResult);
      
      if (duplicateResult && duplicateResult.summary.duplicates_found > 0) {
        // Found duplicates - show modal for user decision
        console.log(`[Chat] Found ${duplicateResult.summary.duplicates_found} duplicate(s), showing modal`);
        const duplicatesWithFiles: DuplicateCheckResult[] = [];
        
        for (const file of fileArray) {
          const result = duplicateResult.results[file.name];
          if (result && result.has_duplicate) {
            duplicatesWithFiles.push({
              filename: file.name,
              file: file,
              duplicates: result.existing_files,
              action: 'reuse', // Default to reuse
              selectedExistingId: result.existing_files[0]?.id
            });
          }
        }
        
        setDuplicateCheckResults(duplicatesWithFiles);
        setPendingUploadFiles(fileArray);
        setShowDuplicateModal(true);
        
        // Clear the input so the same file can be selected again
        event.target.value = '';
        return;
      }
      
      // No duplicates - proceed with normal upload
      await uploadFiles(fileArray);
    }
    // Clear the input so the same file can be selected again
    event.target.value = '';
  };

  // Handle duplicate modal confirmation
  const handleDuplicateModalConfirm = async (decisions: DuplicateCheckResult[]) => {
    console.log('[Chat] Duplicate modal confirmed with decisions:', decisions);
    setShowDuplicateModal(false);
    
    const filesToUpload: File[] = [];
    const reusedDocs: any[] = [];
    
    // Get all pending files that weren't duplicates (they need to be uploaded)
    const duplicateFilenames = new Set(decisions.map(d => d.filename));
    const nonDuplicateFiles = pendingUploadFiles.filter(f => !duplicateFilenames.has(f.name));
    filesToUpload.push(...nonDuplicateFiles);
    
    // Process user decisions for duplicates
    for (const decision of decisions) {
      if (decision.action === 'reupload') {
        // User wants to re-upload - first delete ALL existing versions
        const file = pendingUploadFiles.find(f => f.name === decision.filename);
        if (file) {
          // Delete all existing duplicates before re-uploading
          for (const existingFile of decision.duplicates) {
            console.log(`[Chat] Deleting existing file before re-upload: ${existingFile.filename} (ID: ${existingFile.id})`);
            const deleted = await deleteExistingUpload(existingFile.id);
            if (deleted) {
              console.log(`[Chat] ✅ Successfully deleted existing file: ${existingFile.id}`);
            } else {
              console.warn(`[Chat] ⚠️ Failed to delete existing file: ${existingFile.id}`);
            }
          }
          filesToUpload.push(file);
        }
      } else if (decision.action === 'reuse' && decision.selectedExistingId) {
        // User wants to reuse existing document
        console.log(`[Chat] Reusing existing document: ${decision.filename} (ID: ${decision.selectedExistingId})`);
        const reusedDoc = await reuseDocument(decision.selectedExistingId);
        if (reusedDoc) {
          reusedDocs.push({
            id: reusedDoc.id,
            fileId: reusedDoc.id,
            filename: reusedDoc.filename,
            type: 'document',
            uploaded: true,
            uploading: false,
            processing: false,
            processingStatus: 'completed',
            reused: true,
            // Excel multi-sheet support
            available_sheets: reusedDoc.available_sheets,
            sheet_name: reusedDoc.sheet_name
          });
          console.log(`[Chat] Successfully reused document: ${reusedDoc.filename}`);
        }
      }
    }
    
    // Add reused documents to shared files
    if (reusedDocs.length > 0) {
      setSharedUploadedFiles(prev => [...prev, ...reusedDocs]);
      setPdfUploaded(true);
    }
    
    // Upload files that need uploading
    if (filesToUpload.length > 0) {
      await uploadFiles(filesToUpload);
    }
    
    // Clear pending state
    setPendingUploadFiles([]);
    setDuplicateCheckResults([]);
  };

  // Handle duplicate modal close (cancel)
  const handleDuplicateModalClose = () => {
    setShowDuplicateModal(false);
    setPendingUploadFiles([]);
    setDuplicateCheckResults([]);
  };

  const handleDocumentManagerChange = (documents: any[]) => {
    setSharedUploadedFiles(documents);
    setPdfUploaded(documents.length > 0);
  };

  // Convert sharedUploadedFiles to DocumentManager format
  const convertToDocumentManagerFormat = (files: any[]) => {
    const getFileType = (filename: string): 'document' | 'image' | 'text' | 'spreadsheet' => {
      const extension = filename.toLowerCase().split('.').pop() || '';
      
      // Image files
      if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension)) {
        return 'image';
      }
      
      // Text files
      if (['txt', 'md', 'rtf'].includes(extension)) {
        return 'text';
      }
      
      // Spreadsheet files
      if (['xls', 'xlsx', 'csv'].includes(extension)) {
        return 'spreadsheet';
      }
      
      // Default to document
      return 'document';
    };

    return files.map(file => {
      const filename = file.filename || file.file?.name || 'Unknown file';
      return {
        id: file.id || '',
        fileId: file.fileId || file.id, // For async tracking
        filename,
        type: getFileType(filename),
        size: file.file?.size || file.size,
        uploadDate: new Date().toISOString(),
        preview: file.preview,
        // Preserve processing status fields
        uploading: file.uploading,
        uploaded: file.uploaded,
        processing: file.processing,
        processingProgress: file.processingProgress,
        processingStatus: file.processingStatus,
        error: file.error,
        // Excel multi-sheet support
        available_sheets: file.available_sheets,
        sheet_name: file.sheet_name
      };
    });
  };

  // Function to handle question submission and check for file uploads

  return (
    <div className={styles.container} role="main">
      {/* Hidden file input for direct upload (same as drag and drop) */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf,.docx,.xlsx,.xls,.xlsm,.txt,.md,.png,.jpg,.jpeg"
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
      />
      {/* Hidden file input for direct translation (skip analysis) */}
      <input
        ref={translateFileInputRef}
        type="file"
        multiple
        accept=".pdf,.docx,.doc,.pptx,.ppt,.xlsx,.xls,.xlsm,.txt,.html,.htm,.csv,.rtf,.odt,.odp,.ods,.md"
        style={{ display: 'none' }}
        onChange={handleTranslateFileInputChange}
      />
      
      {showAuthMessage ? (
        <Stack className={styles.chatEmptyState}>
          <ShieldLockRegular
            className={styles.chatIcon}
            style={{ color: 'darkorange', height: '200px', width: '200px' }}
          />
          <h1 className={styles.chatEmptyStateTitle}>Authentication Not Configured</h1>
          <h2 className={styles.chatEmptyStateSubtitle}>
            This app does not have authentication configured. Please add an identity provider by finding your app in the{' '}
            <a href="https://portal.azure.com/" target="_blank">
              Azure Portal
            </a>
            and following{' '}
            <a
              href="https://learn.microsoft.com/en-us/azure/app-service/scenario-secure-app-authentication-app-service#3-configure-authentication-and-authorization"
              target="_blank">
              these instructions
            </a>
            .
          </h2>
          <h2 className={styles.chatEmptyStateSubtitle} style={{ fontSize: '20px' }}>
            <strong>Authentication configuration takes a few minutes to apply. </strong>
          </h2>
          <h2 className={styles.chatEmptyStateSubtitle} style={{ fontSize: '20px' }}>
            <strong>If you deployed in the last 10 minutes, please wait and reload the page after 10 minutes.</strong>
          </h2>
        </Stack>
      ) : (
        <Stack horizontal className={`${styles.chatRoot} ${appStateContext?.state.isChatHistoryOpen ? styles.chatRootWithHistory : ''}`} style={{ flex: 1, overflow: 'hidden' }}>
          {/* Main Chat Container - shrinks when document panel is open, hides for TenderBuilder */}
          <div 
            className={`${styles.chatContainer} ${isDragOverChat ? styles.dragOverChat : ''}`} 
            style={{ 
              marginLeft: 0, 
              flex: showDocumentDraftPanel 
                ? (currentWorkspace?.workspaceMode === 'tender' 
                    ? '0 0 0%'  // Hide chat completely for TenderBuilder (full screen)
                    : (isDocumentPanelCollapsed ? 'calc(100% - 44px)' : '0 0 55%'))
                : 1, 
              display: showDocumentDraftPanel && currentWorkspace?.workspaceMode === 'tender' ? 'none' : 'flex',
              transition: 'flex 0.25s ease' 
            }}
            onDragOver={handleChatDragOver}
            onDragLeave={handleChatDragLeave}
            onDrop={handleChatDrop}
          >
            {/* Only render Chat UI */}
            {/* Imagen toolbar pinned above chat UI */}
            {selectedModel && selectedModel.toLowerCase().includes('imagen') && (
              <div className={styles.chatToolbar}>
                <span className={styles.toolbarLabel}>Imagen options:</span>
                <label className={styles.toolbarControl}>
                  Aspect Ratio
                  <select value={imagenAspectRatio} onChange={(e) => setImagenAspectRatio(e.target.value)}>
                    <option value="1:1">1:1 (Square)</option>
                    <option value="16:9">16:9 (Landscape)</option>
                    <option value="9:16">9:16 (Portrait)</option>
                    <option value="4:3">4:3</option>
                    <option value="3:4">3:4</option>
                  </select>
                </label>
                <label className={styles.toolbarControl}>
                  Images
                  <input type="number" min={1} max={4} value={imagenCount} onChange={(e) => setImagenCount(parseInt(e.target.value || '1', 10))} />
                </label>
              </div>
            )}
            {/* GPT Image 1.5 toolbar pinned above chat UI */}
            {selectedModel && (selectedModel.toLowerCase().includes('gpt-image') || selectedModel.toLowerCase().includes('dall-e')) && (
              <div className={styles.chatToolbar}>
                <span className={styles.toolbarLabel}>Image options:</span>
                <label className={styles.toolbarControl}>
                  Size
                  <select value={dalleSize} onChange={(e) => setDalleSize(e.target.value)}>
                    <option value="">Admin Default</option>
                    <option value="1024x1024">1024×1024 (Square)</option>
                    <option value="1024x1792">1024×1792 (Portrait)</option>
                    <option value="1792x1024">1792×1024 (Landscape)</option>
                  </select>
                </label>
                <label className={styles.toolbarControl}>
                  Quality
                  <select value={dalleQuality} onChange={(e) => setDalleQuality(e.target.value)}>
                    <option value="">Admin Default</option>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </label>
              </div>
            )}
            {!messages || (messages.length < 1 && !isLoading && !appStateContext?.state.currentChat) ? (
                  <Stack className={styles.chatEmptyState}>
                    {logo && <img src={buildMediaUrl(logo)} className={styles.chatIcon} aria-hidden="true" />}
                    <h1 className={styles.chatEmptyStateTitle}>{greeting}</h1>
                    <h2 className={styles.chatEmptyStateSubtitle}>How can I help you today?</h2>
                    
                    {/* ThinkingBubble shows during loading with hardcoded flow */}
                    {showLoadingMessage && (
                      <ThinkingBubble 
                        reasoning={intentReasoning || { data_source: 'analyzing', reason: '', confidence: 0, thinking: '' }}
                      />
                    )}
                    
                    {/* 🌍 Document Translation Panel */}
                    {showTranslationPanel && sharedUploadedFiles && sharedUploadedFiles.length > 0 && (
                      <DocumentTranslationPanel
                        uploadedFiles={sharedUploadedFiles.map(f => ({
                          id: f.id || f.file_id,
                          name: f.name || f.filename || 'document',
                          file: f.file,
                          type: f.type || f.content_type,
                          size: f.size
                        }))}
                        detectedLanguage={detectedTargetLanguage || undefined}
                        userMessage={translationPanelMessage}
                        onTranslationComplete={(result) => {
                          // Don't hide panel - let user see the download UI
                          // Only log completion; panel handles download and cleanup
                          console.log('[Chat] Translation completed:', result.success ? 'success' : 'failed', result);
                        }}
                        onDismiss={async () => {
                          // User clicked "Use AI Instead" - upload files through normal flow
                          setShowTranslationPanel(false);
                          setTranslationPanelMessage('');
                          
                          // Get the File objects from pending translation files
                          const filesToUpload = sharedUploadedFiles
                            .filter(f => f.file instanceof File)
                            .map(f => f.file as File);
                          
                          if (filesToUpload.length > 0) {
                            // Clear the temporary translation file entries
                            setSharedUploadedFiles([]);
                            // Upload through normal flow for AI analysis
                            console.log('[Chat] "Use AI Instead" - uploading files for AI analysis:', filesToUpload.map(f => f.name));
                            await uploadFiles(filesToUpload);
                          }
                        }}
                      />
                    )}
                    
                    {/* 📄 PDF to Word Conversion Panel */}
                    {showPdfConversionPanel && sharedUploadedFiles && sharedUploadedFiles.length > 0 && (
                      <PdfConversionPanel
                        uploadedFiles={sharedUploadedFiles.map(f => ({
                          id: f.id || f.file_id,
                          name: f.name || f.filename || 'document',
                          filename: f.filename || f.name,
                          file: f.file,
                          type: f.type || f.content_type,
                          size: f.size
                        }))}
                        onConversionComplete={(result) => {
                          console.log('[Chat] PDF conversion completed:', result.success ? 'success' : 'failed');
                        }}
                        onDismiss={() => {
                          setShowPdfConversionPanel(false);
                        }}
                      />
                    )}
                    
                    {/* 📄 Document Generation Panel */}
                    {showDocumentGenerationPanel && documentGenerationResult && (
                      <DocumentGenerationPanel
                        result={documentGenerationResult}
                        onDismiss={() => {
                          setShowDocumentGenerationPanel(false);
                        }}
                      />
                    )}
                    
                    {/* 📄 PDF to Word Conversion Notification (Empty State) */}
                    {pdfConversionNotification && (
                      <div style={{ marginBottom: '16px', width: '100%', maxWidth: '600px' }}>
                        <MessageBar
                          messageBarType={
                            pdfConversionNotification.status === 'converting' ? MessageBarType.info :
                            pdfConversionNotification.status === 'success' ? MessageBarType.success :
                            MessageBarType.error
                          }
                          isMultiline={true}
                          onDismiss={() => setPdfConversionNotification(null)}
                          dismissButtonAriaLabel="Close conversion notification"
                          styles={{
                            root: pdfConversionNotification.status === 'success' ? {
                              background: 'linear-gradient(90deg, #2b579a15 0%, #3a6eb515 100%)',
                              borderLeft: '4px solid #2b579a'
                            } : undefined
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {pdfConversionNotification.status === 'converting' && (
                              <>
                                <span style={{ fontSize: '16px' }}>⏳</span>
                                <span>Converting <strong>{pdfConversionNotification.filename}</strong> to Word...</span>
                              </>
                            )}
                            {pdfConversionNotification.status === 'success' && (
                              <>
                                <span style={{ fontSize: '18px' }}>📄</span>
                                <span style={{ flex: 1 }}>
                                  <strong>Word document ready!</strong>
                                </span>
                                <a
                                  href={pdfConversionNotification.downloadUrl}
                                  download={pdfConversionNotification.filename}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '6px 16px',
                                    backgroundColor: '#2b579a',
                                    color: 'white',
                                    borderRadius: '4px',
                                    textDecoration: 'none',
                                    fontWeight: 500,
                                    fontSize: '13px'
                                  }}
                                >
                                  ⬇ Download {pdfConversionNotification.filename}
                                </a>
                              </>
                            )}
                            {pdfConversionNotification.status === 'error' && (
                              <>
                                <span style={{ fontSize: '16px' }}>❌</span>
                                <span>Conversion failed: {pdfConversionNotification.error}</span>
                              </>
                            )}
                          </div>
                        </MessageBar>
                      </div>
                    )}
                    
                    {/* QuestionInput for empty state */}
                    {!showLoadingMessage && (
                      <>
                        {/* Compact Document Manager above input */}
                        {(sharedUploadedFiles && sharedUploadedFiles.length > 0) || isUploadingFiles ? (
                          <div style={{ marginBottom: '8px', width: '100%', maxWidth: '1000px' }}>
                            <DocumentManager
                              documents={convertToDocumentManagerFormat(sharedUploadedFiles)}
                              onDocumentsChange={handleDocumentManagerChange}
                              onAddDocument={handleDocumentManagerAdd}
                              disabled={isLoading}
                              maxDocuments={10}
                              compact={true}
                              isUploading={isUploadingFiles}
                              uploadingFilesCount={uploadingFilesCount}
                            />
                          </div>
                        ) : null}
                        <QuestionInput
                        ref={questionInputEmptyRef}
                        clearOnSend
                        placeholder={hasFilesProcessing ? "Waiting for files to finish processing..." : "Ask me anything..."}
                        disabled={isLoading || hasFilesProcessing}
                        isLoading={isLoading}
                        onStop={stopGenerating}
                        hideFilePreview={true}
                        onSend={(question, id, files) => {
                          // If Imagen model is selected, generate images instead of chat
                          if (selectedModel && selectedModel.toLowerCase().includes('imagen')) {
                            const q = typeof question === 'string' ? question : (Array.isArray(question) ? question.map((p: any) => p.text || '').join('\n') : String(question));
                            makeImagenRequest(q);
                            return;
                          }
                          // If GPT Image or DALL-E model is selected, generate images instead of chat
                          if (selectedModel && (selectedModel.toLowerCase().includes('gpt-image') || selectedModel.toLowerCase().includes('dall-e'))) {
                            const q = typeof question === 'string' ? question : (Array.isArray(question) ? question.map((p: any) => p.text || '').join('\n') : String(question));
                            makeDalleRequest(q);
                            return;
                          }
                          // Check if we have files (either from input or shared state)
                          const hasFiles = (files && files.length > 0) || (sharedUploadedFiles && sharedUploadedFiles.length > 0);
                          const questionText = typeof question === 'string' ? question : String(question);
                          
                          // 📄 PDF to Word Conversion Detection
                          // Check if user wants to convert PDF to Word
                          // Note: Document translation is now only available via the Upload dropdown
                          const pdfFile = (sharedUploadedFiles || []).find(f => {
                            const name = f.filename || f.name || '';
                            return name.toLowerCase().endsWith('.pdf');
                          });
                          if (hasFiles && pdfFile && detectPdfToWordIntent(questionText, true, true)) {
                            // Convert directly without showing panel
                            handlePdfToWordConversion(pdfFile);
                            console.log('[Chat] PDF to Word conversion started');
                            return;
                          }
                          
                          if (hasFiles) {
                            setPdfUploaded(true);
                            // Let the backend decide whether to use documents based on intent analysis
                            // Use regular chat flow and pass documents - backend will decide routing
                            console.log('[Chat] Files present, using regular chat with document context - files:', files?.length || 0, 'shared:', sharedUploadedFiles?.length || 0);
                            const conversationId = appStateContext?.state.currentChat?.id || id;
                            
                            makeApiRequestWithCosmosDB(question, conversationId)
                          } else {
                            // No files available, use regular chat
                            setPdfUploaded(false);
                            // Use existing conversation ID if we have one
                            const conversationId = appStateContext?.state.currentChat?.id || id;

                            
                            makeApiRequestWithCosmosDB(question, conversationId)
                          }
                        }}
                        conversationId={appStateContext?.state.currentChat?.id ? appStateContext?.state.currentChat?.id : undefined}
                        onNewChat={newChat}
                        onFileRemoved={handleFileRemoved}
                        clearFiles={clearQuestionInputFiles}
                        externalUploadedFiles={sharedUploadedFiles}
                        onUploadedFilesChange={setSharedUploadedFiles}
                      />
                      </>
                    )}
                    
                    {/* Integrated Chat Controls with all buttons and preferences */}
                    <ChatControls
                      ui={ui}
                      modelDropdownOpen={modelDropdownOpen}
                      selectedDataSource={selectedDataSource}
                      selectedModel={selectedModel}
                      modelOptions={modelOptions}
                      onNewChat={newChat}
                      onFileUpload={() => fileInputRef.current?.click()}
                      onTranslateUpload={() => translateFileInputRef.current?.click()}
                      onModelDropdownToggle={() => setModelDropdownOpen(!modelDropdownOpen)}
                      onModelSelection={handleModelSelection}
                      onDataSourceChange={handleDataSourceSelection}
                      onPreferencesChange={handleUserPreferencesChange}
                      onDocumentWorkspaceCreated={(workspace) => {
                        // Open the document draft panel with the created workspace
                        setCurrentDraftId(workspace.id);
                        setCurrentWorkspace(workspace as TenderWorkspace);  // Track workspace for mode detection
                        setShowDocumentDraftPanel(true);
                      }}
                      onEditDocumentWorkspace={async (workspaceId) => {
                        // Fetch workspace to check if it's a tender
                        try {
                          const workspace = await getWorkspace(workspaceId);
                          setCurrentDraftId(workspaceId);
                          setCurrentWorkspace(workspace as TenderWorkspace);  // Track workspace (mode detection)
                          setShowDocumentDraftPanel(true);
                          setIsDocumentPanelCollapsed(false);
                        } catch (error) {
                          console.error('Failed to load workspace:', error);
                        }
                      }}
                      onPinDocumentWorkspace={async (workspaceId) => {
                        // Pin the workspace and open it
                        setPinnedWorkspaceId(workspaceId);
                        setCurrentDraftId(workspaceId);
                        setShowDocumentDraftPanel(true);
                        setIsDocumentPanelCollapsed(false);
                        // Fetch workspace to set currentWorkspace for mode detection
                        try {
                          const workspace = await getWorkspace(workspaceId);
                          setCurrentWorkspace(workspace as TenderWorkspace);
                        } catch (error) {
                          console.error('Failed to load workspace for mode detection:', error);
                        }
                        // Persist to backend if we have a conversation
                        const convId = appStateContext?.state.currentChat?.id;
                        if (convId) {
                          await updatePinnedWorkspace(convId, workspaceId);
                        }
                      }}
                      pinnedWorkspaceId={pinnedWorkspaceId}
                    />
                  </Stack>
                ) : (
                  <>
                    {/* 🎯 Document Removal Notification */}
                    {documentRemovalNotification && (
                      <div style={{ marginBottom: '16px', padding: '0 20px' }}>
                        <MessageBar
                          messageBarType={MessageBarType.warning}
                          isMultiline={true}
                          onDismiss={() => setDocumentRemovalNotification(null)}
                          dismissButtonAriaLabel="Close document removal notification"
                        >
                          <div>
                            <strong>{documentRemovalNotification.message}</strong>
                            <br />
                            <span style={{ fontSize: '13px', color: '#666' }}>
                              Reason: {documentRemovalNotification.reason} 
                              {documentRemovalNotification.removed_count > 0 && 
                                ` (${documentRemovalNotification.removed_count} document${documentRemovalNotification.removed_count > 1 ? 's' : ''} removed)`
                              }
                            </span>
                          </div>
                        </MessageBar>
                      </div>
                    )}
                    
                    {/* 📄 PDF to Word Conversion Notification */}
                    {pdfConversionNotification && (
                      <div style={{ marginBottom: '16px', padding: '0 20px' }}>
                        <MessageBar
                          messageBarType={
                            pdfConversionNotification.status === 'converting' ? MessageBarType.info :
                            pdfConversionNotification.status === 'success' ? MessageBarType.success :
                            MessageBarType.error
                          }
                          isMultiline={true}
                          onDismiss={() => setPdfConversionNotification(null)}
                          dismissButtonAriaLabel="Close conversion notification"
                          styles={{
                            root: pdfConversionNotification.status === 'success' ? {
                              background: 'linear-gradient(90deg, #2b579a15 0%, #3a6eb515 100%)',
                              borderLeft: '4px solid #2b579a'
                            } : undefined
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {pdfConversionNotification.status === 'converting' && (
                              <>
                                <span style={{ fontSize: '16px' }}>⏳</span>
                                <span>Converting <strong>{pdfConversionNotification.filename}</strong> to Word...</span>
                              </>
                            )}
                            {pdfConversionNotification.status === 'success' && (
                              <>
                                <span style={{ fontSize: '18px' }}>📄</span>
                                <span style={{ flex: 1 }}>
                                  <strong>Word document ready!</strong>
                                </span>
                                <a
                                  href={pdfConversionNotification.downloadUrl}
                                  download={pdfConversionNotification.filename}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '6px 16px',
                                    backgroundColor: '#2b579a',
                                    color: 'white',
                                    borderRadius: '4px',
                                    textDecoration: 'none',
                                    fontWeight: 500,
                                    fontSize: '13px'
                                  }}
                                >
                                  ⬇ Download {pdfConversionNotification.filename}
                                </a>
                              </>
                            )}
                            {pdfConversionNotification.status === 'error' && (
                              <>
                                <span style={{ fontSize: '16px' }}>❌</span>
                                <span>Conversion failed: {pdfConversionNotification.error}</span>
                              </>
                            )}
                          </div>
                        </MessageBar>
                      </div>
                    )}
                    
                    <div className={styles.chatMessageStream} style={{ marginBottom: isLoading ? '40px' : '0px' }} role="log">
                      {/* Render all conversation messages */}
                      {messages
                        .sort((a, b) => {
                          // Sort messages by timestamp to ensure proper conversational order
                          const timeA = new Date(a.date || 0).getTime();
                          const timeB = new Date(b.date || 0).getTime();
                          return timeA - timeB;
                        })
                        .map((msg, index) => {
                        const key = (msg as any).id || `${msg.role}-${index}`;
                        if (msg.role === 'user') {
                          let userText = typeof msg.content === 'string' ? msg.content : '';
                          
                          // Transform clarification response format to friendly display
                          // Format: [CLARIFICATION_RESPONSE:type:option_id] Label → "Search in: Label"
                          const clarificationMatch = userText.match(/^\[CLARIFICATION_RESPONSE:(\w+):[\w-]+\]\s*(.+)$/);
                          if (clarificationMatch) {
                            const [, clarificationType, label] = clarificationMatch;
                            if (clarificationType === 'source_ambiguity') {
                              userText = `🔍 ${label}`;
                            } else if (clarificationType === 'intent_clarification') {
                              userText = `✨ ${label}`;
                            } else {
                              userText = label; // Just show the label for other types
                            }
                          }
                          
                          // RTL detection for user messages (same logic as QuestionInput)
                          const rtlChar = /[\u0590-\u05FF\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\u0700-\u074F]/g;
                          const alphabeticChar = /[\u0041-\u005A\u0061-\u007A\u0590-\u05FF\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\u0700-\u074F\u00C0-\u024F\u1E00-\u1EFF]/g;
                          const rtlMatches = userText.match(rtlChar) || [];
                          const alphabeticMatches = userText.match(alphabeticChar) || [];
                          
                          let isUserMessageRtl = false;
                          if (alphabeticMatches.length >= 2) {
                            const rtlPercentage = (rtlMatches.length / alphabeticMatches.length) * 100;
                            const startsWithHebrew = /^[\u0590-\u05FF]/.test(userText.trim());
                            const hasSignificantHebrew = rtlMatches.length > 3 && rtlPercentage > 30;
                            isUserMessageRtl = rtlPercentage > 30 || (startsWithHebrew && rtlPercentage > 20);
                          }
                          
                          return (
                            <div key={key} className={styles.chatMessageUser}>
                              <div 
                                className={styles.chatMessageUserMessage} 
                                style={{ 
                                  direction: isUserMessageRtl ? 'rtl' : 'ltr', 
                                  textAlign: isUserMessageRtl ? 'right' : 'left',
                                  position: 'relative'
                                }}
                              >
                                {userText}
                                <MessageActions 
                                  message={msg} 
                                  conversationTitle={appStateContext?.state.currentChat?.title}
                                  className={styles.userMessageActions}
                                />
                              </div>
                            </div>
                          );
                        }
                        if (msg.role === 'assistant') {
                          const images = (msg as any).images;
                          if (Array.isArray(images) && images.length > 0) {
                            return (
                              <div key={key} className={styles.chatMessageGpt}>
                                <div>
                                  <div style={{ margin: '6px 0 10px', fontSize: 13, opacity: 0.8 }}>
                                    {typeof msg.content === 'string' ? msg.content : 'Images'}
                                  </div>
                                  <div className={styles.imageGrid}>
                                    {images.map((img: any, idx: number) => {
                                      const mime = img.mime_type || 'image/png';
                                      const src = `data:${mime};base64,${img.b64}`;
                                      const ext = (mime.split('/')[1] || 'png').split(';')[0];
                                      const filename = `imagen-${idx + 1}.${ext}`;
                                      return (
                                        <div key={idx} className={styles.imageCard} onClick={() => setPreviewImage({ src, alt: `Generated Image ${idx + 1}` })}>
                                          <img className={styles.imageThumb} src={src} alt={`Generated Image ${idx + 1}`} />
                                          <div style={{ position: 'absolute', top: 6, right: 6, display: 'flex', gap: 6, background: 'rgba(255,255,255,0.85)', borderRadius: 6, padding: '2px 4px' }} onClick={(e) => e.stopPropagation()}>
                                            <IconButton
                                              iconProps={{ iconName: 'Download' }}
                                              title="Download"
                                              ariaLabel="Download image"
                                              onClick={() => downloadDataUrl(src, filename)}
                                              styles={{ 
                                                root: { 
                                                  height: 40, 
                                                  width: 40, 
                                                  border: '1px solid #667eea',
                                                  borderRadius: '4px',
                                                  backgroundColor: '#f8f9fa',
                                                  '&:hover': { backgroundColor: '#ede9fe', borderColor: '#764ba2' }
                                                }, 
                                                icon: { fontSize: 16, color: '#667eea' } 
                                              }}
                                            />
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          const contentStr = typeof msg.content === 'string' ? msg.content : '';
                          
                          // RTL detection for assistant messages
                          const rtlChar = /[\u0590-\u05FF\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\u0700-\u074F]/g;
                          const alphabeticChar = /[\u0041-\u005A\u0061-\u007A\u0590-\u05FF\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\u0700-\u074F\u00C0-\u024F\u1E00-\u1EFF]/g;
                          const rtlMatches = contentStr.match(rtlChar) || [];
                          const alphabeticMatches = contentStr.match(alphabeticChar) || [];
                          
                          let isAssistantMessageRtl = false;
                          if (alphabeticMatches.length >= 2) {
                            const rtlPercentage = (rtlMatches.length / alphabeticMatches.length) * 100;
                            const startsWithHebrew = /^[\u0590-\u05FF]/.test(contentStr.trim());
                            
                            // Special handling for calendar/meeting responses to avoid incorrect RTL alignment
                            const isCalendarResponse = /(?:meeting|calendar|event|appointment|schedule|participant|attendee|organizer)/i.test(contentStr) ||
                                                       /(?:📅|🕐|👤|👥|📍|📝|⏰|🗓️)/g.test(contentStr);
                            
                            if (rtlPercentage > 50) {
                              // Always honor Hebrew majority regardless of calendar context
                              isAssistantMessageRtl = true;
                            } else if (isCalendarResponse) {
                              // For calendar responses with mixed content, be more conservative
                              isAssistantMessageRtl = startsWithHebrew && rtlPercentage > 35;
                            } else {
                              // Standard RTL detection for other content
                              isAssistantMessageRtl = rtlPercentage > 30 || (startsWithHebrew && rtlPercentage > 20);
                            }
                          }
                          
                          return (
                            <div 
                              key={key} 
                              className={styles.chatMessageGpt} 
                              style={{ position: 'relative' }}
                              data-rtl={isAssistantMessageRtl}
                              data-message-id={msg.id}
                            >
                              <Answer
                                answer={{
                                  answer: contentStr,
                                  citations: (() => {
                                    if (Array.isArray((msg as any).citations)) return (msg as any).citations;
                                    return parseCitationFromMessage(messages[index - 1]);
                                  })(),
                                  unified_citations: (msg as any).unified_citations,
                                  generated_chart: (() => {
                                    const directChart = (msg as any).generated_chart;
                                    if (directChart) {
                                      // Parse JSON string if it's a string
                                      if (typeof directChart === 'string') {
                                        try {
                                          return JSON.parse(directChart);
                                        } catch (e) {
                                          console.error('Failed to parse chart JSON:', e);
                                        }
                                      } else {
                                        return directChart;
                                      }
                                    }
                                    return parsePlotFromMessage(msg) || parsePlotFromMessage(messages[index - 1]);
                                  })(),
                                  generated_dashboard: (() => {
                                    const directDashboard = (msg as any).generated_dashboard;
                                    if (directDashboard) {
                                      // Parse JSON string if it's a string
                                      if (typeof directDashboard === 'string') {
                                        try {
                                          return JSON.parse(directDashboard);
                                        } catch (e) {
                                          console.error('Failed to parse dashboard JSON:', e);
                                        }
                                      } else {
                                        return directDashboard;
                                      }
                                    }
                                    return null;
                                  })(),
                                  generated_document: (msg as any).generated_document,
                                  message_id: (msg as any).id,
                                  feedback: (msg as any).feedback,
                                  model: (msg as any).model
                                }}
                                source={(msg as any).source}
                                sourceConfidence={(msg as any).sourceConfidence}
                                document_metadata={(msg as any).document_metadata}
                                onCitationClicked={c => onShowCitation(c)}
                                onExectResultClicked={() => onShowExecResult(answerId)}
                                onEditDocument={(workspaceId) => {
                                  console.log('📝 Opening document editor for workspace:', workspaceId);
                                  setCurrentDraftId(workspaceId);
                                  setShowDocumentDraftPanel(true);
                                }}
                                simple={true}
                                intent_reasoning={(msg as any).intent_reasoning}
                                showThinkingProcess={showThinkingProcess}
                                isCached={(msg as any).isCached || (msg as any).source?.startsWith('faq_cache:')}
                                showCacheBadge={showCacheBadge}
                              />
                              <MessageActions 
                                message={msg} 
                                conversationTitle={appStateContext?.state.currentChat?.title}
                                className={styles.assistantMessageActions}
                              />
                            </div>
                          );
                        }
                        if (msg.role === ERROR) {
                          return (
                            <div key={key} className={styles.chatMessageError}>
                              <Stack horizontal className={styles.chatMessageErrorContent}>
                                <ErrorCircleRegular className={styles.errorIcon} style={{ color: 'rgba(182, 52, 67, 1)' }} />
                                <span>Error</span>
                              </Stack>
                              <span className={styles.chatMessageErrorContent}>{typeof msg.content === 'string' ? msg.content : ''}</span>
                            </div>
                          );
                        }
                        return null;
                      })}
                      
                      {/* ThinkingBubble shows during loading with hardcoded flow */}
                      {showLoadingMessage && (
                        <ThinkingBubble 
                          reasoning={intentReasoning || { data_source: 'analyzing', reason: '', confidence: 0, thinking: '' }}
                        />
                      )}
                      
                      {/* 🌍 Document Translation Panel */}
                      {showTranslationPanel && sharedUploadedFiles && sharedUploadedFiles.length > 0 && (
                        <DocumentTranslationPanel
                          uploadedFiles={sharedUploadedFiles.map(f => ({
                            id: f.id || f.file_id,
                            name: f.name || f.filename || 'document',
                            file: f.file,
                            type: f.type || f.content_type,
                            size: f.size
                          }))}
                          detectedLanguage={detectedTargetLanguage || undefined}
                          userMessage={translationPanelMessage}
                          onTranslationComplete={(result) => {
                            // Don't hide panel - let user see the download UI
                            // Panel handles download and cleanup
                            console.log('[Chat] Translation completed:', result.success ? 'success' : 'failed', result);
                          }}
                          onDismiss={async () => {
                            // User clicked "Use AI Instead" - upload files through normal flow
                            setShowTranslationPanel(false);
                            setTranslationPanelMessage('');
                            
                            // Get the File objects from pending translation files
                            const filesToUpload = sharedUploadedFiles
                              .filter(f => f.file instanceof File)
                              .map(f => f.file as File);
                            
                            if (filesToUpload.length > 0) {
                              // Clear the temporary translation file entries
                              setSharedUploadedFiles([]);
                              // Upload through normal flow for AI analysis
                              console.log('[Chat] "Use AI Instead" - uploading files for AI analysis:', filesToUpload.map(f => f.name));
                              await uploadFiles(filesToUpload);
                            }
                          }}
                        />
                      )}
                      
                      {/* 📄 PDF to Word Conversion Panel */}
                      {showPdfConversionPanel && sharedUploadedFiles && sharedUploadedFiles.length > 0 && (
                        <PdfConversionPanel
                          uploadedFiles={sharedUploadedFiles.map(f => ({
                            id: f.id || f.file_id,
                            name: f.name || f.filename || 'document',
                            filename: f.filename || f.name,
                            file: f.file,
                            type: f.type || f.content_type,
                            size: f.size
                          }))}
                          onConversionComplete={(result) => {
                            console.log('[Chat] PDF conversion completed:', result.success ? 'success' : 'failed');
                          }}
                          onDismiss={() => {
                            setShowPdfConversionPanel(false);
                          }}
                        />
                      )}
                      
                      {/* 📄 Document Generation Panel */}
                      {showDocumentGenerationPanel && documentGenerationResult && (
                        <DocumentGenerationPanel
                          result={documentGenerationResult}
                          onDismiss={() => {
                            setShowDocumentGenerationPanel(false);
                          }}
                        />
                      )}
                      
                      {/* Streaming response display */}
                      {isStreaming && streamingContent && (
                        <div className={styles.chatMessageGpt} style={{ position: 'relative' }}>
                          <Answer
                            answer={{
                              answer: streamingContent,
                              citations: [],
                              message_id: 'streaming',
                              generated_chart: null,
                              generated_dashboard: null
                            }}
                            source="streaming"
                            simple={true}
                            onCitationClicked={() => {}}
                            onExectResultClicked={() => {}}
                          />
                        </div>
                      )}
                      
                      {/* Clarification Cards - when system needs user to select source/intent */}
                      {pendingClarification && (
                        <div className={styles.chatMessageGpt} style={{ position: 'relative' }}>
                          <ClarificationCards
                            clarification={pendingClarification}
                            onSelect={(optionId, option) => {
                              // Build clarification response message
                              const responseText = `[CLARIFICATION_RESPONSE:${pendingClarification.type}:${optionId}] ${option.label}`;
                              setPendingClarification(null);
                              // Send as new user message
                              makeApiRequestWithCosmosDB(responseText, appStateContext?.state.currentChat?.id);
                            }}
                            language={pendingClarification.prompt_he ? 'he' : 'en'}
                            disabled={isLoading}
                          />
                        </div>
                      )}
                      
                      {(isUploadingFiles || hasFilesProcessing) && (
                        <div className={styles.uploadingIndicator}>
                          <div className={styles.uploadingIcon}>📄</div>
                          <div className={styles.uploadingText}>
                            {isUploadingFiles ? 'Uploading files...' : 'Processing files... Please wait.'}
                          </div>
                        </div>
                      )}
                      <div ref={chatMessageStreamEnd} />
                    </div>

                    {/* Document Manager for managing uploaded documents in chat */}
                    {(sharedUploadedFiles && sharedUploadedFiles.length > 0) || isUploadingFiles ? (
                      <div style={{ padding: '0 20px', marginBottom: '4px' }}>
                        <DocumentManager
                          documents={convertToDocumentManagerFormat(sharedUploadedFiles)}
                          onDocumentsChange={handleDocumentManagerChange}
                          onAddDocument={handleDocumentManagerAdd}
                          disabled={isLoading}
                          maxDocuments={10}
                          compact={true}
                          isUploading={isUploadingFiles}
                          uploadingFilesCount={uploadingFilesCount}
                        />
                      </div>
                    ) : null}

                    {/* Chat input for ongoing conversations */}
                    <Stack horizontal className={styles.chatInput} verticalAlign="center" style={{ width: '100%', padding: '16px 20px 8px 20px' }}>
                      <div style={{ flex: 1 }}>
                        <QuestionInput
                          ref={questionInputChatRef}
                          clearOnSend
                          placeholder={hasFilesProcessing ? "Waiting for files to finish processing..." : "Type a new question..."}
                          disabled={isLoading || hasFilesProcessing}
                          isLoading={isLoading}
                          onStop={stopGenerating}
                          hideFilePreview={true}
                          onSend={(question, id, files) => {
                            console.log('[Chat] onSend called with:', { question, id, files, isLoading });
                            console.log('[Chat] Current conversation ID:', appStateContext?.state.currentChat?.id);
                            console.log('[Chat] CosmosDB available:', appStateContext?.state.isCosmosDBAvailable?.cosmosDB);
                            
                            // If Imagen model is selected, generate images instead of chat
                            if (selectedModel && selectedModel.toLowerCase().includes('imagen')) {
                              const q = typeof question === 'string' ? question : (Array.isArray(question) ? question.map((p: any) => p.text || '').join('\n') : String(question));
                              makeImagenRequest(q);
                              return;
                            }
                            // If GPT Image or DALL-E model is selected, generate images instead of chat
                            if (selectedModel && (selectedModel.toLowerCase().includes('gpt-image') || selectedModel.toLowerCase().includes('dall-e'))) {
                              const q = typeof question === 'string' ? question : (Array.isArray(question) ? question.map((p: any) => p.text || '').join('\n') : String(question));
                              makeDalleRequest(q);
                              return;
                            }
                            // Check if we have files (either from input or shared state)
                            const hasFiles = (files && files.length > 0) || (sharedUploadedFiles && sharedUploadedFiles.length > 0);
                            const questionText = typeof question === 'string' ? question : String(question);
                            
                            // 📄 PDF to Word Conversion Detection
                            // Note: Document translation is now only available via the Upload dropdown
                            const pdfFile = (sharedUploadedFiles || []).find(f => {
                              const name = f.filename || f.name || '';
                              return name.toLowerCase().endsWith('.pdf');
                            });
                            if (hasFiles && pdfFile && detectPdfToWordIntent(questionText, true, true)) {
                              // Convert directly without showing panel
                              handlePdfToWordConversion(pdfFile);
                              console.log('[Chat] PDF to Word conversion started');
                              return;
                            }
                            
                            if (hasFiles) {
                              console.log('[Chat] Processing file upload');
                              setPdfUploaded(true);
                              // Let the backend decide whether to use documents based on intent analysis
                              // Use regular chat flow and pass documents - backend will decide routing
                              console.log('[Chat] Files present, using regular chat with document context - files:', files?.length || 0, 'shared:', sharedUploadedFiles?.length || 0);
                              // Use existing conversation ID if we have one
                              const conversationId = appStateContext?.state.currentChat?.id || id;

                              
                              makeApiRequestWithCosmosDB(question as string, conversationId)
                            } else {
                              // No files available, use regular chat
                              console.log('[Chat] Processing regular chat message');
                              setPdfUploaded(false);
                              // Use existing conversation ID if we have one
                              const conversationId = appStateContext?.state.currentChat?.id || id;

                              
                              makeApiRequestWithCosmosDB(question, conversationId)
                            }
                          }}
                          conversationId={
                            appStateContext?.state.currentChat?.id ? appStateContext?.state.currentChat?.id : undefined
                          }
                          onNewChat={newChat}
                          onFileRemoved={handleFileRemoved}
                          clearFiles={clearQuestionInputFiles}
                          externalUploadedFiles={sharedUploadedFiles}
                          onUploadedFilesChange={setSharedUploadedFiles}
                        />
                      </div>
                    </Stack>
                    
                    {/* Chat Controls for ongoing conversations - same as home page */}
                    <div style={{ padding: '0 20px 16px 20px' }}>
                      <ChatControls
                        ui={ui}
                        modelDropdownOpen={modelDropdownOpen}
                        selectedDataSource={selectedDataSource}
                        selectedModel={selectedModel}
                        modelOptions={modelOptions}
                        onNewChat={newChat}
                        onFileUpload={() => fileInputRef.current?.click()}
                        onTranslateUpload={() => translateFileInputRef.current?.click()}
                        onModelDropdownToggle={() => setModelDropdownOpen(!modelDropdownOpen)}
                        onModelSelection={handleModelSelection}
                        onDataSourceChange={handleDataSourceSelection}
                        onPreferencesChange={handleUserPreferencesChange}
                        onDocumentWorkspaceCreated={(workspace) => {
                          // Open the document draft panel with the created workspace
                          setCurrentDraftId(workspace.id);
                          setCurrentWorkspace(workspace as TenderWorkspace);  // Track workspace for mode detection
                          setShowDocumentDraftPanel(true);
                        }}
                        onEditDocumentWorkspace={async (workspaceId) => {
                          // Fetch workspace to check if it's a tender
                          try {
                            const workspace = await getWorkspace(workspaceId);
                            setCurrentDraftId(workspaceId);
                            setCurrentWorkspace(workspace as TenderWorkspace);  // Track workspace (mode detection)
                            setShowDocumentDraftPanel(true);
                            setIsDocumentPanelCollapsed(false);
                          } catch (error) {
                            console.error('Failed to load workspace:', error);
                          }
                        }}
                        onPinDocumentWorkspace={async (workspaceId) => {
                          // Pin the workspace and open it
                          setPinnedWorkspaceId(workspaceId);
                          setCurrentDraftId(workspaceId);
                          setShowDocumentDraftPanel(true);
                          setIsDocumentPanelCollapsed(false);
                          // Fetch workspace to set currentWorkspace for mode detection
                          try {
                            const workspace = await getWorkspace(workspaceId);
                            setCurrentWorkspace(workspace as TenderWorkspace);
                          } catch (error) {
                            console.error('Failed to load workspace for mode detection:', error);
                          }
                          // Persist to backend if we have a conversation
                          const convId = appStateContext?.state.currentChat?.id;
                          if (convId) {
                            await updatePinnedWorkspace(convId, workspaceId);
                          }
                        }}
                        pinnedWorkspaceId={pinnedWorkspaceId}
                        style={{ marginTop: '8px' }}
                      />
                    </div>
                </>
                )}
            {/* Placeholder for Notebook and other sections */}
          </div>
      
      {/* Chat History Panel */}
      {messages && messages.length > 0 && isCitationPanelOpen && activeCitation && (
            <Stack.Item className={styles.citationPanel} tabIndex={0} role="tabpanel" aria-label="Citations Panel">
              <Stack
                aria-label="Citations Panel Header Container"
                horizontal
                className={styles.citationPanelHeaderContainer}
                horizontalAlign="space-between"
                verticalAlign="center">
                <span aria-label="Citations" className={styles.citationPanelHeader}>
                  Citations
                </span>
                <IconButton
                  iconProps={{ iconName: 'Cancel' }}
                  aria-label="Close citations panel"
                  onClick={() => setIsCitationPanelOpen(false)}
                  styles={{ 
                    root: { 
                      height: 40, 
                      width: 40, 
                      border: '1px solid #6b7280',
                      borderRadius: '4px',
                      backgroundColor: '#f8f9fa',
                      '&:hover': { backgroundColor: '#e5e7eb', borderColor: '#4b5563' }
                    }, 
                    icon: { fontSize: 16, color: '#6b7280' } 
                  }}
                />
              </Stack>
              <h5
                className={styles.citationPanelTitle}
                tabIndex={0}
                title={
                  activeCitation.url && isClickableUrl(activeCitation.url)
                    ? activeCitation.url
                    : activeCitation.title ?? ''
                }
                onClick={() => onViewSource(activeCitation)}>
                {activeCitation.title}
              </h5>
              <div tabIndex={0}>
                <ReactMarkdown
                  linkTarget="_blank"
                  className={styles.citationPanelContent}
                  children={DOMPurify.sanitize(activeCitation.content, { ALLOWED_TAGS: XSSAllowTags })}
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                />
              </div>
            </Stack.Item>
          )}
          {messages && messages.length > 0 && isIntentsPanelOpen && (
            <Stack.Item className={styles.citationPanel} tabIndex={0} role="tabpanel" aria-label="Intents Panel">
              <Stack
                aria-label="Intents Panel Header Container"
                horizontal
                className={styles.citationPanelHeaderContainer}
                horizontalAlign="space-between"
                verticalAlign="center">
                <span aria-label="Intents" className={styles.citationPanelHeader}>
                  Intents
                </span>
                <IconButton
                  iconProps={{ iconName: 'Cancel' }}
                  aria-label="Close intents panel"
                  onClick={() => setIsIntentsPanelOpen(false)}
                  styles={{ 
                    root: { 
                      height: 40, 
                      width: 40, 
                      border: '1px solid #6b7280',
                      borderRadius: '4px',
                      backgroundColor: '#f8f9fa',
                      '&:hover': { backgroundColor: '#e5e7eb', borderColor: '#4b5563' }
                    }, 
                    icon: { fontSize: 16, color: '#6b7280' } 
                  }}
                />
              </Stack>
              <Stack horizontalAlign="space-between">
                {appStateContext?.state?.answerExecResult[answerId]?.map((execResult: ExecResults, index) => (
                  <Stack className={styles.exectResultList} verticalAlign="space-between">
                    <><span>Intent:</span> <p>{execResult.intent}</p></>
                    {execResult.search_query && <><span>Search Query:</span>
                      <SyntaxHighlighter
                        style={nord}
                        wrapLines={true}
                        lineProps={{ style: { wordBreak: 'break-all', whiteSpace: 'pre-wrap' } }}
                        language="sql"
                        PreTag="p">
                        {execResult.search_query}
                      </SyntaxHighlighter></>}
                    {execResult.search_result && <><span>Search Result:</span> <p>{execResult.search_result}</p></>}
                    {execResult.code_generated && <><span>Code Generated:</span>
                      <SyntaxHighlighter
                        style={nord}
                        wrapLines={true}
                        lineProps={{ style: { wordBreak: 'break-all', whiteSpace: 'pre-wrap' } }}
                        language="python"
                        PreTag="p">
                        {execResult.code_generated}
                      </SyntaxHighlighter>
                    </>}
                  </Stack>
                ))}
              </Stack>
            </Stack.Item>
          )}
          
          {/* 📝 Document Draft Co-Authoring Side Panel or Tender Builder */}
          {showDocumentDraftPanel && currentDraftId && (
            <div style={{ 
              flex: currentWorkspace?.workspaceMode === 'tender' 
                ? '1 1 100%'  // TenderBuilder takes full width
                : (isDocumentPanelCollapsed ? '0 0 44px' : '0 0 45%'), 
              height: '100%', 
              overflow: 'hidden',
              transition: 'flex 0.25s ease'
            }}>
              {currentWorkspace?.workspaceMode === 'tender' ? (
                // Tender mode: Show TenderBuilder with 3-pane layout
                <TenderBuilder
                  workspace={currentWorkspace}
                  onClose={() => {
                    setShowDocumentDraftPanel(false);
                    setCurrentDraftId(null);
                    setCurrentWorkspace(null);
                  }}
                  onWorkspaceUpdated={(updatedWorkspace) => {
                    setCurrentWorkspace(updatedWorkspace);
                  }}
                />
              ) : (
                // Standard mode: Show DocumentDraftPanel
                <DocumentDraftPanel
                  draft={null}
                  conversationId={appStateContext?.state.currentChat?.id || ''}
                  workspaceId={currentDraftId}
                  refreshKey={draftRefreshKey}
                  sidePanel={true}
                  isCollapsed={isDocumentPanelCollapsed}
                  isPinned={pinnedWorkspaceId === currentDraftId}
                  hasUpdate={hasDocumentUpdate}
                  onCollapse={() => setIsDocumentPanelCollapsed(true)}
                  onExpand={() => setIsDocumentPanelCollapsed(false)}
                  onClearUpdate={() => setHasDocumentUpdate(false)}
                  onPin={async () => {
                    setPinnedWorkspaceId(currentDraftId);
                    const convId = appStateContext?.state.currentChat?.id;
                    if (convId && currentDraftId) {
                      await updatePinnedWorkspace(convId, currentDraftId);
                    }
                  }}
                  onUnpin={async () => {
                    setPinnedWorkspaceId(null);
                    const convId = appStateContext?.state.currentChat?.id;
                    if (convId) {
                      await updatePinnedWorkspace(convId, null);
                    }
                  }}
                  onClose={() => {
                    // If pinned, just collapse instead of closing
                    if (pinnedWorkspaceId === currentDraftId) {
                      setIsDocumentPanelCollapsed(true);
                    } else {
                      setShowDocumentDraftPanel(false);
                      setCurrentDraftId(null);
                      setCurrentWorkspace(null);
                    }
                  }}
                />
              )}
            </div>
          )}
          
          {/* Image preview dialog (global) */}
          <Dialog
            hidden={!previewImage}
            onDismiss={() => setPreviewImage(null)}
            dialogContentProps={{ type: DialogType.close, title: previewImage?.alt || 'Preview' }}
            minWidth={200}
            maxWidth={800}
          >
            {previewImage && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                  <IconButton
                    iconProps={{ iconName: 'Download' }}
                    title="Download"
                    ariaLabel="Download image"
                    onClick={() => {
                      const alt = previewImage.alt || 'imagen';
                      const ext = previewImage.src.split(';')[0].split('/')[1] || 'png';
                      downloadDataUrl(previewImage.src, `${alt}.${ext}`);
                    }}
                    styles={{ 
                      root: { 
                        height: 40, 
                        width: 40, 
                        border: '1px solid #667eea',
                        borderRadius: '4px',
                        backgroundColor: '#f8f9fa',
                        '&:hover': { backgroundColor: '#ede9fe', borderColor: '#764ba2' }
                      }, 
                      icon: { fontSize: 16, color: '#667eea' } 
                    }}
                  />
                </div>
                <img src={previewImage.src} alt={previewImage.alt} style={{ maxWidth: '100%', height: 'auto', borderRadius: 6 }} />
              </div>
            )}
          </Dialog>
        </Stack>
      )}
      {appStateContext?.state.isCosmosDBAvailable?.status === CosmosDBStatus.Working && 
       appStateContext?.state.isChatHistoryOpen && 
       <ChatHistoryPanel />}
      
      {/* Duplicate File Detection Modal */}
      <DuplicateFileModal
        isOpen={showDuplicateModal}
        duplicateFiles={duplicateCheckResults}
        onClose={handleDuplicateModalClose}
        onConfirm={handleDuplicateModalConfirm}
        isLoading={isCheckingDuplicates}
      />
      
      {/* Excel Sheet Selector Modal - always mounted, visibility controlled by isOpen */}
      <ExcelSheetSelector
        file={pendingExcelFile}
        isOpen={showExcelSheetSelector && pendingExcelFile !== null}
        onConfirm={handleExcelSheetConfirm}
        onCancel={handleExcelSheetCancel}
      />
      
      {/* Token Limit Exceeded Dialog */}
      <Dialog
        hidden={!showTokenLimitDialog}
        onDismiss={() => setShowTokenLimitDialog(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: '⚠️ Token Limit Reached',
          subText: tokenLimitError?.reason || 'You have reached your token usage limit.'
        }}
        modalProps={{
          isBlocking: true,
          styles: { main: { maxWidth: 500 } }
        }}
      >
        <Stack tokens={{ childrenGap: 16 }}>
          <MessageBar messageBarType={MessageBarType.blocked}>
            <strong>Limit Type:</strong> {tokenLimitError?.blockedBy === 'user' ? 'Personal Limit' : 
              tokenLimitError?.blockedBy === 'group' ? 'Group Limit' : 
              tokenLimitError?.blockedBy === 'organization' ? 'Organization Limit' : 'Usage Limit'}
          </MessageBar>
          <Text>
            Your AI chat access has been temporarily restricted because you've reached your allocated token limit.
          </Text>
          <Text variant="small" style={{ color: '#666' }}>
            Token limits reset periodically based on your configuration. If you need increased limits, 
            please contact your system administrator.
          </Text>
        </Stack>
        <DialogFooter>
          <PrimaryButton onClick={() => setShowTokenLimitDialog(false)} text="OK" />
        </DialogFooter>
      </Dialog>
    </div>
  )
}

export default Chat
